import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Clock3,
  Copy,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Repeat,
  Trash2,
  Users,
  VideoIcon,
  X,
} from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import MeetingSetupOverlay from '../components/common/MeetingSetupOverlay'
import { useToast } from '../components/common/Toast'
import { useAuth } from '../context/AuthContext'
import { useZoomOAuth } from '../context/ZoomOAuthContext'
import { authAPI, internalAgendaAPI, meetingsAPI, teamsAPI, zoomAPI } from '../services/api'
import { getDraftMeeting, removeDraftMeeting } from '../services/draftStorage'
import { requestOnboardingVideosCheck } from '../constants/onboardingVideos'
import {
  QUICK_START_DEFAULT_GOVERNANCE_CONFIG,
  getQuickStartOrganizerTimezone,
  normalizeQuickStartMeetingResponse,
} from '../utils/quickStartFlow'
import { decodeHtmlEntities } from '../utils/displayText'
import { useScheduleConflicts } from '../hooks/useScheduleConflicts'
import ScheduleConflictBanner from '../components/common/ScheduleConflictBanner'
import {
  buildInternalAgendaStateFromGoals,
  emptyInternalAgendaState,
  INTERNAL_DESIRED_OUTCOMES,
  mapApiInternalAgendaToGoals,
  newInternalGoalId,
} from '../utils/internalAgendaBuilder'
import {
  AGENDA_QUALITY_HINTS,
  INPUT_QUALITY_HINTS,
  scoreAgendaOverallQuality,
  scoreTitleTextQuality,
} from '../utils/meetingInputQuality'
import MeetingInputQualityIndicator from '../components/meetingBuilder/MeetingInputQualityIndicator'
import AgendaItemUploadButton from '../components/meetingBuilder/AgendaItemUploadButton'
import MeetingAttachmentsSummaryList from '../components/meetingBuilder/MeetingAttachmentsSummaryList'
import BuilderDatePicker from '../components/meetingBuilder/BuilderDatePicker'
import DurationMinutesStepper from '../components/meetingBuilder/DurationMinutesStepper'
import BuilderStepInlineError from '../components/meetingBuilder/BuilderStepInlineError'
import StepShell from '../components/meetingBuilder/StepShell'
import ChoiceCard from '../components/meetingBuilder/ChoiceCard'
import {
  BUILDER_CHOICE_CARD_SELECTED,
  BUILDER_CHOICE_CARD_UNSELECTED,
  builderCompactPrimaryButtonClassName,
  builderInputClassName,
  builderMeetingTypeTileClassName,
  builderPanelClassName,
  durationQuickOptionPillClassName,
} from '../components/meetingBuilder/builderFieldStyles'
import { INVALID_EMAIL_MESSAGE, isValidEmail } from '../utils/emailValidation'
import { isExternalJoinEnabled } from '../config/externalJoinBeta'
import { MEETING_PLATFORMS } from '../constants/platformBranding'

async function fetchBuilderCompletionCoaching({
  meetingTitle,
  agendaItems,
  meetingDurationMinutes,
  seed,
}) {
  try {
    const res = await meetingsAPI.getBuilderCompletionCoaching({
      meetingTitle,
      agendaItems: (agendaItems ?? []).map((item) => ({
        title: item.title,
        details: String(item?.details ?? '').trim(),
      })),
      meetingDurationMinutes:
        typeof meetingDurationMinutes === 'number' && meetingDurationMinutes > 0
          ? meetingDurationMinutes
          : undefined,
      seed,
    })
    const data = res?.data ?? res
    return {
      coachingTitle: data?.titleMessage ?? null,
      coachingHelper: data?.helperMessage ?? null,
    }
  } catch {
    return { coachingTitle: null, coachingHelper: null }
  }
}

/** Scheduled meeting success screen: auto-redirect after this unless the user closes or navigates away. */
const SCHEDULED_SUCCESS_REDIRECT_MS = 30_000

const BUILDER_STAGES = [
  { id: 'start', label: 'Start' },
  { id: 'meeting-type', label: 'Type' },
  { id: 'title', label: 'Title' },
  { id: 'audience', label: 'Audience' },
  { id: 'people', label: 'People' },
  { id: 'recurrence', label: 'Recurrence' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'agenda', label: 'Agenda' },
  { id: 'review', label: 'Review' },
]

const REVIEW_STAGE_INDEX_RAW = BUILDER_STAGES.findIndex((stage) => stage.id === 'review')
const REVIEW_STAGE_INDEX = REVIEW_STAGE_INDEX_RAW >= 0 ? REVIEW_STAGE_INDEX_RAW : BUILDER_STAGES.length - 1

/** Extra space between the progress bar and step header/content (px). */
const BUILDER_STEP_BODY_OFFSET_MAX_PX = 15

function getBuilderViewportBottomInsetPx() {
  return window.matchMedia('(min-width: 1024px)').matches ? 32 : 16
}

const DEFAULT_DURATION_MINUTES = 30
/** One-tap options for meeting length; values in minutes (1.5 hr = 90). */
const MEETING_DURATION_QUICK_OPTIONS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '45 min', minutes: 45 },
  { label: '60 min', minutes: 60 },
  { label: '1.5 hr', minutes: 90 },
]
/** One-tap topic lengths; use custom field for any other value. */
const AGENDA_TOPIC_DURATION_QUICK_OPTIONS = [
  { label: '5 min', minutes: 5 },
  { label: '10 min', minutes: 10 },
  { label: '15 min', minutes: 15 },
  { label: '20 min', minutes: 20 },
  { label: '30 min', minutes: 30 },
  { label: '45 min', minutes: 45 },
  { label: '60 min', minutes: 60 },
]
const DEFAULT_AGENDA_ITEM_MINUTES = 10
const FUTURE_SUCCESS_THRESHOLD_MINUTES = 15
const DEFAULT_RECURRENCE_RULE = Object.freeze({
  type: 2,
  repeat_interval: 1,
  weekly_days: '',
  monthly_day: '',
  end_times: 0,
  end_date_time: '',
  never_ends: false,
})

const WEEKDAY_OPTIONS = [
  { label: 'Sun', value: '1' },
  { label: 'Mon', value: '2' },
  { label: 'Tue', value: '3' },
  { label: 'Wed', value: '4' },
  { label: 'Thu', value: '5' },
  { label: 'Fri', value: '6' },
  { label: 'Sat', value: '7' },
]

/** Calendar days 1–31 for monthly recurrence (Zoom `monthly_day`). */
const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

function ordinalDayLabel(n) {
  const v = n % 100
  if (v >= 11 && v <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

function buildId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

function buildEmptyAgendaItem(overrides = {}) {
  return {
    id: buildId('agenda'),
    title: '',
    details: '',
    durationMinutes: '',
    ...overrides,
  }
}

const TITLE_EMPTY_ERROR = 'Add a meeting title to continue.'

const AGENDA_INLINE_TITLE_ERROR_MESSAGES = new Set([
  'Add at least one agenda item before continuing.',
  'Every agenda item needs a title.',
])

const AGENDA_DURATION_ERROR = 'Every agenda item needs a duration in minutes.'

function agendaItemsMissingTitleIds(agendaItems) {
  return (agendaItems ?? [])
    .filter((item) => !String(item?.title ?? '').trim())
    .map((item) => item.id)
}

function isAgendaInlineTitleValidationError(message) {
  return AGENDA_INLINE_TITLE_ERROR_MESSAGES.has(message)
}

function agendaItemsMissingDurationIds(agendaItems) {
  return (agendaItems ?? [])
    .filter((item) => String(item?.title ?? '').trim())
    .filter((item) => {
      const m = Number(item.durationMinutes)
      return !Number.isFinite(m) || m <= 0
    })
    .map((item) => item.id)
}

function isAgendaTimeBudgetError(message) {
  return typeof message === 'string' && message.startsWith('Total agenda time')
}

function agendaFieldClassName(hasError) {
  return [
    'rounded-xl border px-3 py-2 text-sm text-gray-900 outline-none transition',
    hasError
      ? 'border-red-400 bg-red-50/80 focus:border-red-500 focus:bg-white focus:ring-2 focus:ring-red-100'
      : 'border-gray-200 bg-gray-50 focus:border-[#16c1b2]/50 focus:bg-white focus:ring-2 focus:ring-[#16c1b2]/10',
  ].join(' ')
}

function isAgendaDurationPresetSelected(durationMinutes, presetMinutes) {
  const n = Number(durationMinutes)
  return Number.isFinite(n) && n > 0 && n === presetMinutes
}

/** Safe attribute selector fragment for `[data-mb-agenda-item-id="…"]`. */
function mbAgendaItemSelector(id) {
  const s = String(id ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `[data-mb-agenda-item-id="${s}"]`
}

/** Scroll an agenda row fully into view inside the builder's agenda list panel. */
function scrollMbAgendaItemIntoView(itemId) {
  const row = document.querySelector(mbAgendaItemSelector(itemId))
  if (!row) return
  const scrollContainer = row.closest('[data-mb-agenda-scroll]')
  if (!(scrollContainer instanceof HTMLElement)) {
    row.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const padding = 4
  const rowRect = row.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const nextScrollTop =
    scrollContainer.scrollTop + (rowRect.top - containerRect.top) - padding

  scrollContainer.scrollTo({
    top: Math.max(0, nextScrollTop),
    behavior: 'smooth',
  })
}

/** Normalize imported agendas so review/submit passes builder validation (titles, durations, total vs meeting length). */
function sanitizeImportedAgendaForBuilder(rows, meetingTitleFallback, meetingDurationBudget) {
  const budget =
    typeof meetingDurationBudget === 'number' && Number.isFinite(meetingDurationBudget) && meetingDurationBudget > 0
      ? meetingDurationBudget
      : DEFAULT_DURATION_MINUTES

  const titled = rows
    .map((row) => ({
      id: row.id,
      title: String(row.title ?? '').trim(),
      details: row.details ?? '',
      durationMinutes: row.durationMinutes,
    }))
    .filter((row) => row.title)

  let resolved =
    titled.length > 0
      ? titled.map((row) => {
          let mins = Number(row.durationMinutes)
          if (!Number.isFinite(mins) || mins <= 0) mins = DEFAULT_AGENDA_ITEM_MINUTES
          return { ...row, durationMinutes: String(Math.max(1, Math.round(mins))) }
        })
      : [
          buildEmptyAgendaItem({
            title: meetingTitleFallback.trim() || 'Agenda discussion',
            durationMinutes: String(budget),
          }),
        ]

  let sum = resolved.reduce((s, r) => s + Number(r.durationMinutes), 0)
  if (budget > 0 && sum > budget && resolved.length > 0) {
    resolved = resolved.map((row) => ({
      ...row,
      durationMinutes: String(Math.max(1, Math.floor((Number(row.durationMinutes) * budget) / sum))),
    }))
    sum = resolved.reduce((s, r) => s + Number(r.durationMinutes), 0)
    if (sum !== budget) {
      resolved = [...resolved]
      const lastIdx = resolved.length - 1
      const adjusted = Number(resolved[lastIdx].durationMinutes) + (budget - sum)
      resolved[lastIdx] = { ...resolved[lastIdx], durationMinutes: String(Math.max(1, adjusted)) }
    }
  }

  return resolved
}

function buildEmptyForm(user) {
  return {
    meetingTypeId: null,
    title: '',
    audienceMode: null,
    participants: [],
    selectedTeamIds: [],
    recurrenceKind: null,
    recurrenceRule: { ...DEFAULT_RECURRENCE_RULE },
    recurringSeriesId: null,
    scheduleMode: null,
    scheduledAtLocal: '',
    durationMinutes: '',
    timezone: getQuickStartOrganizerTimezone(user),
    agendaItems: [buildEmptyAgendaItem()],
  }
}

/** Review step: primary + optional detail for internal goal cards (match public agenda line pattern). */
function getReviewInternalGoalCardLines(goal, formAgendaItems) {
  const linked = formAgendaItems.find((a) => a.id === goal.linkedPublicAgendaItemId)
  const linkedTitle = String(linked?.title ?? '').trim()
  const outcomeLabel = INTERNAL_DESIRED_OUTCOMES.find(
    (o) => o.value === (goal.desiredOutcome || 'gather_information')
  )?.label
  const notes = (goal.privateNotes || '').trim()
  const titleLine = linkedTitle || outcomeLabel || 'Internal goal'
  const detailParts = []
  if (linkedTitle && outcomeLabel) detailParts.push(outcomeLabel)
  if (notes) detailParts.push(notes)
  if (!linkedTitle) {
    return { titleLine, detail: notes || null }
  }
  return { titleLine, detail: detailParts.length > 0 ? detailParts.join(' · ') : null }
}

function Pill({ children, tone = 'gray' }) {
  const toneClass =
    tone === 'teal'
      ? 'bg-[#f0fdfc] text-[#0d8f84] border-[#16c1b2]/20'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700 border-amber-100'
        : 'bg-gray-100 text-gray-700 border-gray-200'
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${toneClass}`}>
      {children}
    </span>
  )
}

function participantInitials(participant) {
  const name = String(participant?.name ?? '').trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return parts[0].slice(0, 2).toUpperCase()
  }
  const email = String(participant?.email ?? '').trim()
  return email.slice(0, 2).toUpperCase() || '?'
}

function ParticipantChip({ participant, userDomain, onRemove }) {
  const isExternal = participant.email && !participant.email.endsWith(`@${userDomain}`)
  return (
    <div className="inline-flex max-w-full items-center gap-2.5 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
          isExternal
            ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-100'
            : 'bg-[#f0fdfc] text-[#0d8f84] ring-1 ring-[#16c1b2]/20'
        }`}
        aria-hidden
      >
        {participantInitials(participant)}
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">
          {participant.name || participant.email}
        </p>
        {participant.name ? (
          <p className="truncate text-xs text-gray-500">{participant.email}</p>
        ) : null}
      </div>
      {isExternal ? <Pill tone="amber">External</Pill> : null}
      {participant.sourceTeamIds?.length > 0 ? <Pill tone="teal">Team</Pill> : null}
      <button
        type="button"
        onClick={() => onRemove(participant.email)}
        className="shrink-0 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
        aria-label={`Remove ${participant.email}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

function getUserDomain(user) {
  const email = user?.email ?? ''
  const at = email.indexOf('@')
  return at >= 0 && at < email.length - 1 ? email.slice(at + 1).toLowerCase() : 'rithmo.ai'
}

function normalizeParticipantEmail(value, userDomain) {
  const trimmed = String(value ?? '').trim().toLowerCase()
  if (!trimmed) return ''
  return trimmed.includes('@') ? trimmed : `${trimmed}@${userDomain}`
}

function guessParticipantNameFromEmail(email) {
  const atIndex = String(email ?? '').indexOf('@')
  const localRaw = atIndex >= 0 ? String(email).slice(0, atIndex) : String(email ?? '')
  if (!localRaw) return ''
  const cleaned = localRaw.split('+')[0].replace(/[._-]+/g, ' ').trim()
  if (!cleaned) return ''
  const tokens = cleaned
    .split(/\s+/)
    .map((token) => token.replace(/^\d+|\d+$/g, ''))
    .filter((token) => token && /[a-zA-Z]/.test(token))
  if (tokens.length === 0) return ''
  const capitalize = (token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
  if (tokens.length === 1) return capitalize(tokens[0])
  return `${capitalize(tokens[0])} ${capitalize(tokens[tokens.length - 1])}`
}

function formatDateTimeLocal(value, timezone) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  if (timezone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(date)
      const get = (type) => parts.find((part) => part.type === type)?.value ?? '00'
      const hour = get('hour') === '24' ? '00' : get('hour')
      return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`
    } catch {
      // Fall back to browser-local formatting below.
    }
  }

  const pad = (number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

function localStringToUtcInTimezone(localDateStr, timezone) {
  try {
    const asUtc = new Date(`${localDateStr}:00Z`)
    if (Number.isNaN(asUtc.getTime())) return null
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(asUtc)
    const get = (type) => parts.find((part) => part.type === type)?.value ?? '00'
    const timezoneLocalAsUtc = new Date(
      `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`
    )
    const offsetMs = timezoneLocalAsUtc.getTime() - asUtc.getTime()
    return new Date(asUtc.getTime() - offsetMs).toISOString()
  } catch {
    const date = new Date(localDateStr)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
}

function formatInTimezone(isoString, timezone, options = {}) {
  if (!isoString) return ''
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options,
    }).format(new Date(isoString))
  } catch {
    return ''
  }
}

function normalizeMeetingTypePresets(rawPresets) {
  const canonicalName = (name) =>
    String(name ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s*\/\s*/g, ' / ')
      .replace(/\s*,\s*/g, ', ')
      .replace(/\s+/g, ' ')

  const renameMap = {
    'weekly team meeting': 'Team meeting',
    'daily stand-up scrum': 'Stand up / scrum',
    'daily stand-up / scrum': 'Stand up / scrum',
    'brainstorm / ideation': 'Brainstorm',
    'workshop / training': 'Training',
    'all hands / town hall': 'All hands',
    'engineering deep dive / design review': 'Design review',
    'incident review p0, p1': 'Incident review',
    'incident review p0 / p1': 'Incident review',
    'interview structured': 'Interview',
  }

  return (rawPresets ?? []).map((preset) => ({
    id: preset?.id ?? preset?.key ?? 'default',
    name:
      renameMap[canonicalName(preset?.name ?? preset?.displayName ?? '')] ??
      preset?.name ??
      preset?.displayName ??
      'Default Meeting',
    description: preset?.description ?? '',
  }))
}

function normalizeParticipant(participant) {
  return {
    id: participant?.id ?? participant?.participantId ?? buildId('participant'),
    name: decodeHtmlEntities(participant?.name ?? participant?.displayName ?? guessParticipantNameFromEmail(participant?.email ?? '')),
    email: String(participant?.email ?? '').trim().toLowerCase(),
    role: participant?.role ?? 'participant',
    manual: true,
    sourceTeamIds: [],
  }
}

function mergeParticipantsFromTeam(currentParticipants, teamMembers, teamId, organizerEmailLower) {
  const byEmail = new Map(
    (currentParticipants ?? [])
      .filter((participant) => participant?.email)
      .map((participant) => [participant.email.toLowerCase(), participant])
  )
  const nextParticipants = [...(currentParticipants ?? [])]

  for (const member of teamMembers ?? []) {
    const email = String(member?.email ?? '').trim().toLowerCase()
    if (!email || email === organizerEmailLower) continue
    const existing = byEmail.get(email)
    if (existing) {
      const sourceTeamIds = Array.isArray(existing.sourceTeamIds) ? existing.sourceTeamIds : []
      if (!sourceTeamIds.includes(teamId)) {
        existing.sourceTeamIds = [...sourceTeamIds, teamId]
      }
      if (!existing.name) {
        existing.name = member?.fullName ?? member?.full_name ?? guessParticipantNameFromEmail(email)
      }
      continue
    }

    const participant = {
      id: buildId('participant'),
      name: member?.fullName ?? member?.full_name ?? guessParticipantNameFromEmail(email),
      email,
      role: 'participant',
      manual: false,
      sourceTeamIds: [teamId],
    }
    byEmail.set(email, participant)
    nextParticipants.push(participant)
  }

  return nextParticipants
}

function removeParticipantsFromTeam(currentParticipants, teamId) {
  return (currentParticipants ?? []).reduce((accumulator, participant) => {
    const sourceTeamIds = Array.isArray(participant.sourceTeamIds)
      ? participant.sourceTeamIds.filter((id) => id !== teamId)
      : []
    const keepBecauseManual = participant.manual === true
    if (keepBecauseManual || sourceTeamIds.length > 0) {
      accumulator.push({
        ...participant,
        sourceTeamIds,
      })
    }
    return accumulator
  }, [])
}

function getEffectiveDurationMinutes(durationMinutes, agendaItems) {
  const explicit = Number(durationMinutes)
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.round(explicit)
  }

  const agendaTotal = (agendaItems ?? []).reduce((total, item) => {
    const value = Number(item?.durationMinutes)
    return total + (Number.isFinite(value) && value > 0 ? Math.round(value) : 0)
  }, 0)

  return agendaTotal > 0 ? agendaTotal : DEFAULT_DURATION_MINUTES
}

function getRequiredDurationMinutes(durationMinutes) {
  const explicit = Number(durationMinutes)
  if (!Number.isFinite(explicit) || explicit <= 0) return null
  return Math.round(explicit)
}

function buildAgendaPayload(agendaItems, fallbackDurationMinutes) {
  const cleanItems = (agendaItems ?? [])
    .map((item, index) => ({
      id: item?.id ?? `agenda_${index + 1}`,
      title: String(item?.title ?? '').trim(),
      description: String(item?.details ?? '').trim(),
      durationMinutes: Number(item?.durationMinutes),
      order: index,
    }))
    .filter((item) => item.title)

  if (cleanItems.length === 0) {
    throw new Error('Add at least one agenda item before creating the meeting.')
  }

  const explicitDurationTotal = cleanItems.reduce(
    (total, item) => total + (Number.isFinite(item.durationMinutes) && item.durationMinutes > 0 ? item.durationMinutes : 0),
    0
  )
  const unspecifiedCount = cleanItems.filter(
    (item) => !Number.isFinite(item.durationMinutes) || item.durationMinutes <= 0
  ).length
  const targetDuration = Math.max(
    Number(fallbackDurationMinutes) || 0,
    explicitDurationTotal,
    DEFAULT_DURATION_MINUTES
  )
  let remainingMinutes = Math.max(targetDuration - explicitDurationTotal, 0)
  const evenFallback = unspecifiedCount > 0
    ? Math.max(DEFAULT_AGENDA_ITEM_MINUTES, Math.floor((remainingMinutes || targetDuration) / unspecifiedCount))
    : 0

  return cleanItems.map((item, index) => {
    let durationMinutes = Number.isFinite(item.durationMinutes) && item.durationMinutes > 0
      ? Math.round(item.durationMinutes)
      : evenFallback

    if ((!Number.isFinite(item.durationMinutes) || item.durationMinutes <= 0) && unspecifiedCount > 0) {
      const remainingUnspecified = cleanItems
        .slice(index)
        .filter((candidate) => !Number.isFinite(candidate.durationMinutes) || candidate.durationMinutes <= 0).length
      if (remainingUnspecified <= 1 && remainingMinutes > 0) {
        durationMinutes = Math.max(DEFAULT_AGENDA_ITEM_MINUTES, remainingMinutes)
      }
      remainingMinutes = Math.max(0, remainingMinutes - durationMinutes)
    }

    return {
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      durationSec: durationMinutes * 60,
      order: item.order,
    }
  })
}

function buildRecurrenceRulePayload(recurrenceKind, recurrenceRule) {
  if (recurrenceKind !== 'recurring') return undefined

  const hasEnd =
    recurrenceRule?.never_ends === true ||
    Number(recurrenceRule?.end_times) > 0 ||
    String(recurrenceRule?.end_date_time ?? '').trim() !== ''

  if (!hasEnd) return undefined

  if (Number(recurrenceRule?.type) === 2) {
    const days = String(recurrenceRule?.weekly_days ?? '')
      .split(',')
      .map((day) => day.trim())
      .filter((day) => /^[1-7]$/.test(day))
    if (days.length === 0) return undefined
  }

  if (Number(recurrenceRule?.type) === 3) {
    const md = Number(recurrenceRule?.monthly_day)
    if (!Number.isInteger(md) || md < 1 || md > 31) return undefined
  }

  const payload = {
    type: Number(recurrenceRule?.type) || 2,
    repeat_interval: Number(recurrenceRule?.repeat_interval) || 1,
  }

  if (payload.type === 2) {
    const weeklyDays = String(recurrenceRule?.weekly_days ?? '')
      .split(',')
      .map((day) => day.trim())
      .filter((day) => /^[1-7]$/.test(day))
    if (weeklyDays.length > 0) {
      payload.weekly_days = [...new Set(weeklyDays)].sort((a, b) => Number(a) - Number(b)).join(',')
    }
  }

  if (payload.type === 3) {
    const md = Number(recurrenceRule?.monthly_day)
    if (Number.isInteger(md) && md >= 1 && md <= 31) {
      payload.monthly_day = md
    }
  }

  if (recurrenceRule?.never_ends === true) {
    payload.never_ends = true
  } else {
    if (Number(recurrenceRule?.end_times) > 0) {
      payload.end_times = Number(recurrenceRule.end_times)
    }
    const endDateTime = String(recurrenceRule?.end_date_time ?? '').trim()
    if (endDateTime) {
      payload.end_date_time = endDateTime
    }
  }

  return payload
}

function resolveSchedulingFieldsForForm(f, user, options = {}) {
  const { forDraft = false } = options
  const timezone = f.timezone || getQuickStartOrganizerTimezone(user)
  const effectiveDuration = forDraft
    ? getEffectiveDurationMinutes(f.durationMinutes, f.agendaItems)
    : getRequiredDurationMinutes(f.durationMinutes)

  if (forDraft) {
    if (f.scheduleMode !== 'later' || !f.scheduledAtLocal) {
      return {
        scheduledStartTime: undefined,
        scheduledEndTime: undefined,
        effectiveDuration,
      }
    }
    const draftStart = localStringToUtcInTimezone(f.scheduledAtLocal, timezone)
    if (!draftStart) {
      return {
        scheduledStartTime: undefined,
        scheduledEndTime: undefined,
        effectiveDuration,
      }
    }
    const draftEnd = new Date(new Date(draftStart).getTime() + effectiveDuration * 60 * 1000).toISOString()
    return {
      scheduledStartTime: draftStart,
      scheduledEndTime: draftEnd,
      effectiveDuration,
    }
  }

  if (f.scheduleMode !== 'now' && f.scheduleMode !== 'later') {
    throw new Error('Choose when the meeting should happen.')
  }

  if (effectiveDuration == null) {
    throw new Error('Add a meeting duration before continuing.')
  }

  const scheduledStartTime =
    f.scheduleMode === 'now'
      ? new Date().toISOString()
      : localStringToUtcInTimezone(f.scheduledAtLocal, timezone)

  if (!scheduledStartTime) {
    throw new Error('Choose a valid date and time before continuing.')
  }

  return {
    scheduledStartTime,
    scheduledEndTime: new Date(new Date(scheduledStartTime).getTime() + effectiveDuration * 60 * 1000).toISOString(),
    effectiveDuration,
  }
}

/**
 * Validations for a given form snapshot (used when advancing with flushSync)
 */
function validateStageWithFormState(stageId, f, { user, forDraft = false }) {
  if (stageId === 'start') {
    return ''
  }
  if (stageId === 'meeting-type' && (f.meetingTypeId == null || f.meetingTypeId === '')) {
    return 'Choose a meeting type to continue.'
  }
  if (stageId === 'title' && !String(f.title ?? '').trim()) {
    return 'Add a meeting title to continue.'
  }
  if (stageId === 'audience' && f.audienceMode == null) {
    return 'Choose an audience to continue.'
  }
  const hasParticipant = (f.participants ?? []).some((participant) => participant?.email)
  if (stageId === 'people' && !forDraft && !hasParticipant) {
    return 'Add at least one participant before continuing.'
  }
  if (stageId === 'recurrence') {
    if (f.recurrenceKind == null || f.recurrenceKind === '') {
      return 'Choose whether this meeting is one-off or recurring.'
    }
    if (f.recurrenceKind === 'recurring') {
      const rule = f.recurrenceRule ?? {}
      if (Number(rule.type) === 2) {
        const dayTokens = String(rule.weekly_days ?? '')
          .split(',')
          .map((d) => d.trim())
          .filter((d) => /^[1-7]$/.test(d))
        if (dayTokens.length === 0) {
          return 'Pick at least one weekday for weekly recurrence.'
        }
      }
      if (Number(rule.type) === 3) {
        const md = Number(rule.monthly_day)
        if (!Number.isInteger(md) || md < 1 || md > 31) {
          return 'Choose which calendar day of the month the meeting repeats on (1–31).'
        }
      }
      const payload = buildRecurrenceRulePayload(f.recurrenceKind, f.recurrenceRule)
      if (!payload) {
        return 'Set how the recurrence ends before continuing.'
      }
    }
  }
  if (stageId === 'schedule' && !forDraft) {
    if (f.scheduleMode !== 'now' && f.scheduleMode !== 'later') {
      return 'Choose when the meeting should happen.'
    }
    if (getRequiredDurationMinutes(f.durationMinutes) == null) {
      return 'Add a meeting duration before continuing.'
    }
    try {
      resolveSchedulingFieldsForForm(f, user, { forDraft: false })
    } catch (error) {
      return error?.message || 'Choose a valid date and time before continuing.'
    }
  }
  if (stageId === 'agenda' && !forDraft) {
    const cleanAgenda = (f.agendaItems ?? []).map((item) => ({
      ...item,
      title: String(item?.title ?? '').trim(),
    }))
    if (cleanAgenda.filter((item) => item.title).length === 0) {
      return 'Add at least one agenda item before continuing.'
    }
    if (cleanAgenda.some((item) => !item.title)) {
      return 'Every agenda item needs a title.'
    }
    if ((f.agendaItems ?? []).some((item) => {
      const m = Number(item.durationMinutes)
      return !Number.isFinite(m) || m <= 0
    })) {
      return 'Every agenda item needs a duration in minutes.'
    }
    const duration = getRequiredDurationMinutes(f.durationMinutes) ?? 0
    const total = (f.agendaItems ?? []).reduce((sum, item) => sum + (Number(item.durationMinutes) || 0), 0)
    if (duration > 0 && total > duration) {
      return `Total agenda time (${total} min) exceeds the meeting duration (${duration} min). Remove items or shorten durations.`
    }
  }

  return ''
}

function describeRecurrence(recurrenceKind, recurrenceRule) {
  if (recurrenceKind !== 'recurring') return 'One-off meeting'

  const interval = Number(recurrenceRule?.repeat_interval) || 1
  if (Number(recurrenceRule?.type) === 1) {
    return interval === 1 ? 'Repeats every day' : `Repeats every ${interval} days`
  }
  if (Number(recurrenceRule?.type) === 3) {
    const md = Number(recurrenceRule?.monthly_day)
    const dayPart =
      Number.isInteger(md) && md >= 1 && md <= 31 ? ` on the ${ordinalDayLabel(md)}` : ''
    return interval === 1
      ? `Repeats monthly${dayPart}`
      : `Repeats every ${interval} months${dayPart}`
  }

  const weeklyDays = String(recurrenceRule?.weekly_days ?? '')
    .split(',')
    .map((day) => WEEKDAY_OPTIONS.find((option) => option.value === day)?.label)
    .filter(Boolean)
  const weeklyLabel = weeklyDays.length > 0 ? weeklyDays.join(', ') : 'selected days'
  return interval === 1 ? `Repeats weekly on ${weeklyLabel}` : `Repeats every ${interval} weeks on ${weeklyLabel}`
}

function determineSuccessMode(scheduledStartTime) {
  if (!scheduledStartTime) return 'join-now'
  const diffMinutes = (new Date(scheduledStartTime).getTime() - Date.now()) / 60000
  return diffMinutes >= FUTURE_SUCCESS_THRESHOLD_MINUTES ? 'scheduled' : 'join-now'
}

/** `mb-title-step`: single Enter = next. Other groups: first Enter commits, second Enter in same group = continue. */
function getMbEnterCommitGroup(element) {
  if (!element) return null
  const g = element.closest?.('[data-mb-enter-group]')?.getAttribute('data-mb-enter-group')?.trim()
  return g || null
}

const SCHEDULE_TIME_SLOT_COUNT = 48

function parseScheduledLocalString(value) {
  if (!value || typeof value !== 'string') return null
  const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/)
  if (!m) return null
  const hour24 = Number(m[2])
  const minute = Number(m[3])
  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) return null
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null
  return { date: m[1], hour24, minute }
}

function snapMinuteToFifteen(minute) {
  const mod = ((minute % 60) + 60) % 60
  const choices = [0, 15, 30, 45]
  return choices.reduce((best, c) => (Math.abs(mod - c) < Math.abs(mod - best) ? c : best), 0)
}

/**
 * 48 slots: 12:00…11:45 on the 12h clock, four 15-min steps per “hour” index 0..11.
 */
function hour24AndMinuteToPeriodSlot(hour24, minute) {
  const m = snapMinuteToFifteen(minute)
  const quarter = m / 15
  let period
  let h12
  if (hour24 === 0) {
    period = 'am'
    h12 = 0
  } else if (hour24 < 12) {
    period = 'am'
    h12 = hour24
  } else if (hour24 === 12) {
    period = 'pm'
    h12 = 0
  } else {
    period = 'pm'
    h12 = hour24 - 12
  }
  return { period, slotIndex: h12 * 4 + quarter }
}

function periodSlotToHour24(period, slotIndex) {
  const h12 = Math.floor(slotIndex / 4)
  const quarter = slotIndex % 4
  const minute = quarter * 15
  if (period === 'am') {
    return { hour24: h12 === 0 ? 0 : h12, minute }
  }
  return { hour24: h12 === 0 ? 12 : h12 + 12, minute }
}

function buildScheduledLocalFromParts(dateYmd, period, slotIndex) {
  const { hour24, minute } = periodSlotToHour24(period, Math.max(0, Math.min(SCHEDULE_TIME_SLOT_COUNT - 1, slotIndex)))
  return `${dateYmd}T${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function formatTimeSlotLabel(slotIndex) {
  const h12 = Math.floor(slotIndex / 4)
  const m = (slotIndex % 4) * 15
  const displayHour = h12 === 0 ? 12 : h12
  return `${displayHour}:${String(m).padStart(2, '0')}`
}

/** “Now” in the given IANA zone, normalized to 15-minute boundaries for the time list. */
function getDefaultScheduledLocalString(timezone) {
  const raw = formatDateTimeLocal(new Date().toISOString(), timezone)
  const p = parseScheduledLocalString(raw)
  if (!p) {
    const d = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
      snapMinuteToFifteen(d.getMinutes())
    )}`
  }
  const { period, slotIndex } = hour24AndMinuteToPeriodSlot(p.hour24, p.minute)
  return buildScheduledLocalFromParts(p.date, period, slotIndex)
}

function ScheduleLaterDateTime({ value, timezone, onChange, compact = false }) {
  useEffect(() => {
    const p = parseScheduledLocalString(value)
    if (!p) return
    if ([0, 15, 30, 45].includes(p.minute)) return
    const { period: pr, slotIndex: si } = hour24AndMinuteToPeriodSlot(p.hour24, p.minute)
    const next = buildScheduledLocalFromParts(p.date, pr, si)
    if (next !== value) onChange(next)
  }, [onChange, value])

  const { date, period, slotIndex } = useMemo(() => {
    const p = parseScheduledLocalString(value)
    if (p) {
      const { period: pr, slotIndex: si } = hour24AndMinuteToPeriodSlot(p.hour24, p.minute)
      return { date: p.date, period: pr, slotIndex: si }
    }
    const s = getDefaultScheduledLocalString(timezone)
    const p2 = parseScheduledLocalString(s)
    if (!p2) {
      return { date: '2000-01-01', period: 'am', slotIndex: 0 }
    }
    const { period: pr, slotIndex: si } = hour24AndMinuteToPeriodSlot(p2.hour24, p2.minute)
    return { date: p2.date, period: pr, slotIndex: si }
  }, [value, timezone])

  const minDateYmd = useMemo(() => {
    const p = parseScheduledLocalString(getDefaultScheduledLocalString(timezone))
    return p?.date ?? date
  }, [date, timezone])

  const selectedRef = useRef(null)

  useLayoutEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' })
  }, [date, period, slotIndex, value, timezone])

  const applyParts = (nextDate, nextPeriod, nextSlot) => {
    onChange(buildScheduledLocalFromParts(nextDate, nextPeriod, nextSlot))
  }

  const labelClass = compact
    ? 'mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm'
    : 'mb-2 block text-sm font-medium text-gray-700'
  const timeListClass = compact
    ? 'max-h-36 w-full min-w-[9.5rem] overflow-y-auto scroll-smooth rounded-xl border border-gray-200 bg-gray-50/80 py-0.5 sm:max-h-40 sm:min-w-[10.5rem]'
    : 'max-h-72 w-full min-w-[10.5rem] overflow-y-auto scroll-smooth rounded-2xl border border-gray-200 bg-gray-50/80 py-1'
  const rowPad = compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-sm'
  const periodToggleClass = compact ? 'rounded-xl' : 'rounded-2xl'
  const periodBtnPad = compact ? 'px-3 py-2 text-xs sm:px-4 sm:py-2.5 sm:text-sm' : 'px-4 py-3 text-sm'

  return (
    <div className={compact ? 'space-y-4' : 'space-y-5'}>
      <div className="mx-auto w-full max-w-md">
        <BuilderDatePicker
          label="Date"
          value={date}
          minDate={minDateYmd}
          placeholder="Choose a date"
          onChange={(nextDate) => {
            if (!nextDate) return
            applyParts(nextDate, period, slotIndex)
          }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-md flex-row items-start justify-center gap-4 sm:max-w-lg sm:gap-6">
        <div className="min-w-0 flex-1 sm:flex-initial">
          <p className={labelClass}>{compact ? 'Time (15 min)' : 'Time (15 min steps)'}</p>
          <div className={timeListClass}>
            {Array.from({ length: SCHEDULE_TIME_SLOT_COUNT }, (_, i) => {
              const selected = i === slotIndex
              return (
                <button
                  key={i}
                  ref={selected ? selectedRef : null}
                  type="button"
                  onClick={() => applyParts(date, period, i)}
                  className={`block w-full min-w-0 text-left font-medium transition ${rowPad} ${
                    selected
                      ? 'bg-[#e4faf8] text-gray-900'
                      : 'text-gray-700 hover:bg-white/90'
                  }`}
                >
                  {formatTimeSlotLabel(i)}
                </button>
              )
            })}
          </div>
        </div>

        <div className="shrink-0">
          <p className={labelClass}>AM / PM</p>
          <div
            className={`flex flex-col overflow-hidden border border-gray-200 bg-gray-50/80 p-0.5 ${periodToggleClass}`}
          >
            {['am', 'pm'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => applyParts(date, p, slotIndex)}
                className={`min-w-[4.25rem] font-semibold transition ${periodBtnPad} ${
                  period === p
                    ? 'bg-[#16c1b2] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white/90 hover:text-gray-900'
                }`}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}


function MeetingBuilderPage() {
  const { meetingId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const { user } = useAuth()
  const { isConnected: zoomConnected, connectZoomUrl } = useZoomOAuth()

  const [stageIndex, setStageIndex] = useState(0)
  const [form, setForm] = useState(() => buildEmptyForm(user))
  const [meetingTypes, setMeetingTypes] = useState([])
  const [teamsList, setTeamsList] = useState([])
  const [teamsTouched, setTeamsTouched] = useState(false)
  const [startMode, setStartMode] = useState('scratch')
  const [importSearch, setImportSearch] = useState('')
  const [importResults, setImportResults] = useState([])
  const [loadingImportList, setLoadingImportList] = useState(false)
  const [importingMeeting, setImportingMeeting] = useState(false)
  const [knownContactsMap, setKnownContactsMap] = useState(() => new Map())
  const [participantDraft, setParticipantDraft] = useState({ name: '', email: '' })
  const [initializing, setInitializing] = useState(Boolean(meetingId))
  const [loadingCatalog, setLoadingCatalog] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [sendingUpdate, setSendingUpdate] = useState(false)
  const [showSendUpdateConfirm, setShowSendUpdateConfirm] = useState(false)
  const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false)
  const [deletingDraft, setDeletingDraft] = useState(false)
  const [joining, setJoining] = useState(false)
  const [titleFieldError, setTitleFieldError] = useState(null)
  const [stepInlineError, setStepInlineError] = useState(null)
  const [peopleInviteError, setPeopleInviteError] = useState(null)
  const [participantDraftError, setParticipantDraftError] = useState(null)
  const [scheduleDurationError, setScheduleDurationError] = useState(null)
  const [scheduleDateTimeError, setScheduleDateTimeError] = useState(null)
  /** Agenda item ids flagged inline when Done is pressed without a topic title. */
  const [agendaTitleErrorItemIds, setAgendaTitleErrorItemIds] = useState([])
  const [agendaDurationErrorItemIds, setAgendaDurationErrorItemIds] = useState([])
  const [agendaTimeBudgetError, setAgendaTimeBudgetError] = useState(null)
  const [existingMeeting, setExistingMeeting] = useState(null)
  const [attachmentMeetingProvisioning, setAttachmentMeetingProvisioning] = useState(false)
  const ensureAttachmentMeetingInFlightRef = useRef(false)
  const [successState, setSuccessState] = useState(null)
  /** Private host goals; only for `audienceMode === 'external'`, edited in the Agenda step. */
  const [internalGoals, setInternalGoals] = useState([])
  const [internalAgendaLoading, setInternalAgendaLoading] = useState(false)
  const [internalAgendaLoadError, setInternalAgendaLoadError] = useState(null)
  const teamMembersCacheRef = useRef({})
  const pendingJoinWindowRef = useRef(null)
  const redirectTimerRef = useRef(null)
  /** When set, the next Enter in the same `data-mb-enter-group` will advance. */
  const enterCommitPrimedKeyRef = useRef(null)
  const meetingBuilderTitleRef = useRef(null)
  const builderStepBodyRef = useRef(null)
  const [stepBodyOffsetPx, setStepBodyOffsetPx] = useState(BUILDER_STEP_BODY_OFFSET_MAX_PX)
  const [stepBodyMaxHeightPx, setStepBodyMaxHeightPx] = useState(null)
  const [stepBodyOverflows, setStepBodyOverflows] = useState(false)
  /** After Add agenda item: scroll/focus this row once it exists in the DOM. */
  const pendingScrollMbAgendaItemIdRef = useRef(null)
  const [reviewEditing, setReviewEditing] = useState(() => new URLSearchParams(location.search).get('edit') === '1')
  /**
   * True when the builder was opened via the meeting page's "Edit" button (`?edit=1`).
   * In this mode the user expects "Back" to return them to the meeting page, not to
   * walk backwards through the builder wizard or land on the main dashboard.
   */
  const arrivedFromEdit = useMemo(
    () => new URLSearchParams(location.search).get('edit') === '1' && Boolean(meetingId),
    [location.search, meetingId]
  )
  /** Bumped when leaving review edit so summary attachment list refetches. */
  const [attachmentsSummaryVersion, setAttachmentsSummaryVersion] = useState(0)

  // Schedule conflict detection — compute proposed start/end from form state
  const proposedScheduleIso = useMemo(() => {
    if (form.scheduleMode !== 'later' || !form.scheduledAtLocal) return { start: undefined, end: undefined }
    const tz = form.timezone || getQuickStartOrganizerTimezone(user)
    const startIso = localStringToUtcInTimezone(form.scheduledAtLocal, tz)
    if (!startIso) return { start: undefined, end: undefined }
    const dur = Number(form.durationMinutes) || DEFAULT_DURATION_MINUTES
    const endIso = new Date(new Date(startIso).getTime() + dur * 60 * 1000).toISOString()
    return { start: startIso, end: endIso }
  }, [form.scheduleMode, form.scheduledAtLocal, form.timezone, form.durationMinutes, user])

  const {
    overlapping: conflictOverlapping,
    meetingBefore: conflictBefore,
    meetingAfter: conflictAfter,
    loading: conflictsLoading,
  } = useScheduleConflicts(proposedScheduleIso.start, proposedScheduleIso.end, meetingId)

  const organizerEmailLower = String(user?.email ?? '').trim().toLowerCase()
  const userDomain = useMemo(() => getUserDomain(user), [user])
  const recurrenceEndMinDate = useMemo(() => {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  }, [])
  const currentStage = BUILDER_STAGES[stageIndex]?.id ?? 'review'
  const minimumStageIndex = meetingId ? 1 : 0
  useEffect(() => {
    setStepBodyOffsetPx(BUILDER_STEP_BODY_OFFSET_MAX_PX)
    setStepBodyMaxHeightPx(null)
    setStepBodyOverflows(false)
  }, [stageIndex, successState])

  useLayoutEffect(() => {
    if (successState) return

    const stepBodyEl = builderStepBodyRef.current
    if (!stepBodyEl) return

    const measure = () => {
      const foldBottom = window.innerHeight - getBuilderViewportBottomInsetPx()

      stepBodyEl.style.marginTop = '0px'
      stepBodyEl.style.maxHeight = ''
      stepBodyEl.style.overflowY = ''
      const baseTop = stepBodyEl.getBoundingClientRect().top

      let bestOffset = 0
      let bestMaxHeight = 0
      let needsScroll = false

      for (let tryOffset = BUILDER_STEP_BODY_OFFSET_MAX_PX; tryOffset >= 0; tryOffset -= 1) {
        const available = foldBottom - baseTop - tryOffset
        if (available <= 0) continue

        stepBodyEl.style.marginTop = `${tryOffset}px`
        stepBodyEl.style.maxHeight = `${available}px`
        stepBodyEl.style.overflowY = 'auto'

        const bottom = stepBodyEl.getBoundingClientRect().bottom
        if (bottom <= foldBottom + 1) {
          bestOffset = tryOffset
          bestMaxHeight = available
          needsScroll = stepBodyEl.scrollHeight > stepBodyEl.clientHeight + 1
          break
        }
      }

      if (bestMaxHeight === 0) {
        bestMaxHeight = Math.max(0, foldBottom - baseTop)
        needsScroll = true
      }

      setStepBodyOffsetPx((prev) => (prev === bestOffset ? prev : bestOffset))
      setStepBodyMaxHeightPx((prev) => (prev === bestMaxHeight ? prev : bestMaxHeight))
      setStepBodyOverflows((prev) => (prev === needsScroll ? prev : needsScroll))
    }

    measure()
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(stepBodyEl)
    window.addEventListener('resize', measure)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
      stepBodyEl.style.marginTop = ''
      stepBodyEl.style.maxHeight = ''
      stepBodyEl.style.overflowY = ''
    }
  }, [
    successState,
    stageIndex,
    currentStage,
    form.agendaItems,
    form.participants,
    form.recurrenceKind,
    form.scheduleMode,
    startMode,
    stepInlineError,
    reviewEditing,
  ])

  useEffect(() => {
    enterCommitPrimedKeyRef.current = null
  }, [currentStage])

  useLayoutEffect(() => {
    if (successState) return
    if (currentStage !== 'title') return
    meetingBuilderTitleRef.current?.focus()
  }, [currentStage, successState])

  useEffect(() => {
    const pendingId = pendingScrollMbAgendaItemIdRef.current
    if (!pendingId) return
    if (!(form.agendaItems ?? []).some((it) => it.id === pendingId)) {
      pendingScrollMbAgendaItemIdRef.current = null
      return
    }
    pendingScrollMbAgendaItemIdRef.current = null
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollMbAgendaItemIntoView(pendingId)
        const row = document.querySelector(mbAgendaItemSelector(pendingId))
        const titleInput = row?.querySelector('input[placeholder="Topic title"]')
        if (titleInput instanceof HTMLInputElement) {
          titleInput.focus({ preventScroll: true })
        }
      })
    })
  }, [form.agendaItems, currentStage])

  const effectiveDurationMinutes = useMemo(
    () => getEffectiveDurationMinutes(form.durationMinutes, form.agendaItems),
    [form.durationMinutes, form.agendaItems]
  )

  const cleanAgendaItems = useMemo(
    () => (form.agendaItems ?? []).map((item) => ({
      ...item,
      title: String(item?.title ?? '').trim(),
    })),
    [form.agendaItems]
  )

  const reviewListedAgendaItems = useMemo(
    () => cleanAgendaItems.filter((item) => item.title),
    [cleanAgendaItems]
  )
  const reviewMeetingTypeName = useMemo(
    () => meetingTypes.find((m) => m.id === form.meetingTypeId)?.name || 'Default Meeting',
    [meetingTypes, form.meetingTypeId]
  )

  const attachmentMeetingId = useMemo(() => {
    if (existingMeeting?.meetingId && !existingMeeting.isLocalOnly) {
      return existingMeeting.meetingId
    }
    return null
  }, [existingMeeting?.isLocalOnly, existingMeeting?.meetingId])

  const attachmentListScope = useMemo(
    () => (form.recurrenceKind === 'recurring' ? 'series' : 'meeting'),
    [form.recurrenceKind]
  )

  const bumpAttachmentsSummary = useCallback(() => {
    setAttachmentsSummaryVersion((v) => v + 1)
  }, [])

  /** Total minutes already assigned across all agenda items. */
  const totalAgendaMinutes = useMemo(
    () => (form.agendaItems ?? []).reduce((sum, item) => {
      const m = Number(item.durationMinutes)
      return sum + (Number.isFinite(m) && m > 0 ? m : 0)
    }, 0),
    [form.agendaItems]
  )

  /** Meeting duration in minutes (resolved from form). */
  const meetingDuration = getRequiredDurationMinutes(form.durationMinutes) ?? 0

  /** Minutes remaining after subtracting all assigned agenda item durations. */
  const remainingMeetingMinutes = meetingDuration - totalAgendaMinutes

  const titleInputQuality = useMemo(() => scoreTitleTextQuality(form.title), [form.title])

  const agendaInputQuality = useMemo(
    () =>
      scoreAgendaOverallQuality(
        form.agendaItems,
        meetingDuration > 0 ? meetingDuration : effectiveDurationMinutes
      ),
    [form.agendaItems, meetingDuration, effectiveDurationMinutes]
  )

  const hasAtLeastOneParticipant = useMemo(
    () => (form.participants ?? []).some((participant) => participant?.email),
    [form.participants]
  )

  const closePendingJoinWindow = useCallback(() => {
    const pendingWindow = pendingJoinWindowRef.current
    pendingJoinWindowRef.current = null
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.close()
    }
  }, [])

  const takePendingJoinWindow = useCallback(() => {
    const pendingWindow = pendingJoinWindowRef.current
    pendingJoinWindowRef.current = null
    return pendingWindow && !pendingWindow.closed ? pendingWindow : null
  }, [])

  const reserveJoinWindow = useCallback(() => {
    if (pendingJoinWindowRef.current && !pendingJoinWindowRef.current.closed) {
      return pendingJoinWindowRef.current
    }

    const pendingWindow = window.open('', '_blank')
    if (pendingWindow?.document) {
      try {
        pendingWindow.document.title = 'Starting Rithmo meeting...'
        pendingWindow.document.body.innerHTML =
          '<div style="font-family: system-ui, sans-serif; padding: 24px; color: #111827;">Starting your Zoom meeting...</div>'
      } catch {
        // Best effort only.
      }
    }
    pendingJoinWindowRef.current = pendingWindow
    return pendingWindow
  }, [])

  useEffect(() => () => {
    closePendingJoinWindow()
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current)
    }
  }, [closePendingJoinWindow])

  useEffect(() => {
    if (!user?.id) return
    authAPI
      .getKnownContacts()
      .then((res) => {
        const contacts = res?.contacts ?? []
        const map = new Map(contacts.map((c) => [c.email.toLowerCase(), c.display_name]))
        setKnownContactsMap(map)
      })
      .catch(() => setKnownContactsMap(new Map()))
  }, [user?.id])

  useEffect(() => {
    if (currentStage !== 'start' || startMode !== 'import') return

    let cancelled = false
    const timer = window.setTimeout(() => {
      setLoadingImportList(true)
      meetingsAPI
        .listForImport(importSearch)
        .then((response) => {
          if (cancelled) return
          const meetings = response?.data?.meetings ?? response?.meetings ?? []
          setImportResults(
            meetings.filter((meeting) => {
              const candidateId = meeting?.meetingId ?? ''
              return candidateId && candidateId !== meetingId
            })
          )
        })
        .catch(() => {
          if (!cancelled) {
            setImportResults([])
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingImportList(false)
          }
        })
    }, 200)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [currentStage, importSearch, meetingId, startMode])

  useEffect(() => {
    let active = true
    setLoadingCatalog(true)

    Promise.allSettled([
      meetingsAPI.getPresets(),
      teamsAPI.list(),
    ])
      .then(([presetsResult, teamsResult]) => {
        if (!active) return

        if (presetsResult.status === 'fulfilled') {
          const raw = presetsResult.value?.data?.presets ?? presetsResult.value?.presets ?? []
          const normalized = normalizeMeetingTypePresets(raw)
          setMeetingTypes(normalized)
          setForm((prev) => {
            const hasValid = prev.meetingTypeId && normalized.some((preset) => preset.id === prev.meetingTypeId)
            return { ...prev, meetingTypeId: hasValid ? prev.meetingTypeId : null }
          })
        } else {
          setMeetingTypes([{ id: 'default', name: 'Default Meeting', description: 'Standard balanced defaults.' }])
        }

        if (teamsResult.status === 'fulfilled') {
          const rawTeams = teamsResult.value?.data?.teams ?? teamsResult.value?.teams ?? []
          setTeamsList(rawTeams)
        } else {
          setTeamsList([])
        }
      })
      .finally(() => {
        if (active) setLoadingCatalog(false)
      })

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!meetingId) {
      setInitializing(false)
      return
    }

    let active = true
    setInitializing(true)

    meetingsAPI
      .get(meetingId)
      .then((response) => {
        if (!active) return
        const data = response?.data ?? response

        const scheduledStartTime = data?.scheduledStartTime ?? data?.startTime ?? ''
        const scheduledEndTime = data?.scheduledEndTime ?? data?.endTime ?? ''
        const startDate = scheduledStartTime ? new Date(scheduledStartTime) : null
        const endDate = scheduledEndTime ? new Date(scheduledEndTime) : null
        const durationMinutes =
          startDate && endDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())
            ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000))
            : ''

        setStageIndex(REVIEW_STAGE_INDEX)
        setExistingMeeting({
          meetingId: data?.meetingId ?? meetingId,
          isDraft: data?.isDraft === true,
          recurringSeriesId: data?.recurringSeriesId ?? null,
        })
        setForm({
          meetingTypeId: data?.meetingTypeId ?? 'default',
          title: decodeHtmlEntities(data?.title ?? ''),
          audienceMode: data?.audienceMode === 'external' ? 'external' : 'internal',
          participants: (data?.participants ?? [])
            .map((participant) => normalizeParticipant(participant))
            .filter((participant) => participant.email !== organizerEmailLower),
          selectedTeamIds: [],
          recurrenceKind: data?.isRecurring ? 'recurring' : 'one_off',
          recurrenceRule: {
            ...DEFAULT_RECURRENCE_RULE,
            ...(data?.recurrenceRule && typeof data.recurrenceRule === 'object' ? data.recurrenceRule : {}),
          },
          recurringSeriesId: data?.recurringSeriesId ?? null,
          scheduleMode:
            scheduledStartTime && (new Date(scheduledStartTime).getTime() - Date.now()) / 60000 >= FUTURE_SUCCESS_THRESHOLD_MINUTES
              ? 'later'
              : 'now',
          scheduledAtLocal: formatDateTimeLocal(scheduledStartTime, getQuickStartOrganizerTimezone(user)),
          durationMinutes: durationMinutes || '',
          timezone: getQuickStartOrganizerTimezone(user),
          agendaItems: (data?.agenda ?? []).length > 0
            ? (data.agenda ?? []).map((item) => ({
                id: item?.id ?? buildId('agenda'),
                title: decodeHtmlEntities(item?.title ?? ''),
                details: decodeHtmlEntities(item?.description ?? ''),
                durationMinutes:
                  item?.durationSec != null
                    ? Math.max(1, Math.round(Number(item.durationSec) / 60))
                    : '',
              }))
            : [buildEmptyAgendaItem()],
        })
      })
      .catch(() => {
        if (!active) return
        const localDraft = getDraftMeeting(meetingId)
        if (!localDraft) return

        setStageIndex(REVIEW_STAGE_INDEX)
        setExistingMeeting({
          meetingId,
          isDraft: true,
          recurringSeriesId: localDraft.recurringSeriesId ?? null,
          isLocalOnly: true,
        })
        setForm({
          meetingTypeId: localDraft.meetingType ?? 'default',
          title: decodeHtmlEntities(localDraft.title ?? ''),
          audienceMode: 'internal',
          participants: (localDraft.participants ?? [])
            .map((participant) => normalizeParticipant(participant))
            .filter((participant) => participant.email !== organizerEmailLower),
          selectedTeamIds: [],
          recurrenceKind: localDraft.isRecurring ? 'recurring' : 'one_off',
          recurrenceRule: {
            ...DEFAULT_RECURRENCE_RULE,
            ...(localDraft.recurrenceRule && typeof localDraft.recurrenceRule === 'object'
              ? localDraft.recurrenceRule
              : {}),
          },
          recurringSeriesId: localDraft.recurringSeriesId ?? null,
          scheduleMode: localDraft.startTime ? 'later' : 'now',
          scheduledAtLocal: formatDateTimeLocal(localDraft.startTime ?? '', getQuickStartOrganizerTimezone(user)),
          durationMinutes: localDraft.duration ?? '',
          timezone: getQuickStartOrganizerTimezone(user),
          agendaItems: (localDraft.agenda ?? []).length > 0
            ? (localDraft.agenda ?? []).map((item) => ({
                id: item?.id ?? buildId('agenda'),
                title: decodeHtmlEntities(item?.title ?? ''),
                details: decodeHtmlEntities(item?.details ?? ''),
                durationMinutes: item?.duration ?? '',
              }))
            : [buildEmptyAgendaItem()],
        })
      })
      .finally(() => {
        if (active) setInitializing(false)
      })

    return () => {
      active = false
    }
  }, [meetingId, organizerEmailLower, user])

  useEffect(() => {
    if (form.audienceMode === 'internal') {
      setInternalGoals([])
    }
  }, [form.audienceMode])

  useEffect(() => {
    if (!meetingId || initializing) return
    if (form.audienceMode !== 'external') {
      setInternalAgendaLoading(false)
      setInternalAgendaLoadError(null)
      return
    }
    let cancelled = false
    setInternalAgendaLoading(true)
    setInternalAgendaLoadError(null)
    internalAgendaAPI
      .get(meetingId)
      .then((res) => {
        if (cancelled) return
        const data = res?.data ?? res
        const ia = data?.internalAgenda
        if (ia) setInternalGoals(mapApiInternalAgendaToGoals(ia))
        else setInternalGoals([])
      })
      .catch(() => {
        if (!cancelled) {
          setInternalAgendaLoadError(
            'Could not load internal goals. You can still edit the public agenda — try again to load private goals.'
          )
        }
      })
      .finally(() => {
        if (!cancelled) setInternalAgendaLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [form.audienceMode, initializing, meetingId])

  useEffect(() => {
    const importEvent = location.state?.importEvent
    if (!importEvent || meetingId) return

    const startAt = importEvent.startAt ?? importEvent.startTime ?? ''
    const endAt = importEvent.endAt ?? importEvent.endTime ?? ''
    const startDate = startAt ? new Date(startAt) : null
    const endDate = endAt ? new Date(endAt) : null
    const durationMinutes =
      startDate && endDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())
        ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000))
        : ''

    setForm((prev) => ({
      ...prev,
      title: importEvent.title ? decodeHtmlEntities(importEvent.title) : prev.title,
      scheduleMode: startAt ? 'later' : prev.scheduleMode,
      scheduledAtLocal: startAt ? formatDateTimeLocal(startAt, prev.timezone) : prev.scheduledAtLocal,
      durationMinutes: durationMinutes || prev.durationMinutes,
    }))

    navigate(location.pathname, { replace: true, state: {} })
  }, [location.pathname, location.state, meetingId, navigate])

  useEffect(() => {
    if (successState?.mode !== 'scheduled') return

    redirectTimerRef.current = window.setTimeout(() => {
      navigate('/dashboard', { replace: true })
    }, SCHEDULED_SUCCESS_REDIRECT_MS)

    return () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
    }
  }, [navigate, successState])

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    if (key === 'title' && String(value).trim()) {
      setTitleFieldError(null)
    }
    if (key === 'durationMinutes') {
      setScheduleDurationError(null)
    }
    if (key === 'scheduledAtLocal' || key === 'timezone' || key === 'scheduleMode') {
      setScheduleDateTimeError(null)
    }
    if (key === 'recurrenceKind' || key === 'recurrenceRule') {
      setStepInlineError(null)
    }
  }, [])

  const selectScheduleModeLater = useCallback(() => {
    setForm((prev) => {
      const tz = prev.timezone || getQuickStartOrganizerTimezone(user)
      const hasTime = String(prev.scheduledAtLocal ?? '').trim() !== ''
      return {
        ...prev,
        scheduleMode: 'later',
        timezone: tz,
        ...(!hasTime ? { scheduledAtLocal: getDefaultScheduledLocalString(tz) } : {}),
      }
    })
    setScheduleDateTimeError(null)
  }, [user])

  const handleScheduledLocalChange = useCallback(
    (v) => setField('scheduledAtLocal', v),
    [setField]
  )
  const applyImportedMeetingData = useCallback((data) => {
    const importedTeamRaw = data?.teamIds ?? data?.team_ids
    const importedTeamIds = Array.isArray(importedTeamRaw)
      ? importedTeamRaw.map((id) => String(id ?? '').trim()).filter(Boolean)
      : []

    const mappedAgendaItems = (data?.agenda ?? []).map((item, index) => ({
      id: item?.id ?? `agenda_import_${Date.now()}_${index}`,
      title: decodeHtmlEntities(String(item?.title ?? '')),
      details: decodeHtmlEntities(String(item?.description ?? '')),
      durationMinutes:
        item?.durationSec != null
          ? Math.max(1, Math.round(Number(item.durationSec) / 60))
          : Number(item?.duration) > 0
            ? Math.round(Number(item.duration))
            : '',
    }))

    const importedStartTime = data?.scheduledStartTime ?? data?.startTime ?? ''
    const importedEndTime = data?.scheduledEndTime ?? data?.endTime ?? ''
    const importedStartMs = importedStartTime ? new Date(importedStartTime).getTime() : NaN
    const importedEndMs = importedEndTime ? new Date(importedEndTime).getTime() : NaN
    const importedDurationFromSchedule =
      Number.isFinite(importedStartMs) &&
      Number.isFinite(importedEndMs) &&
      importedEndMs > importedStartMs
        ? Math.round((importedEndMs - importedStartMs) / 60000)
        : null
    const importedDurationFromAgenda = mappedAgendaItems.reduce((total, item) => {
      const duration = Number(item.durationMinutes)
      return total + (Number.isFinite(duration) && duration > 0 ? duration : 0)
    }, 0)
    const importedDurationMinutes =
      importedDurationFromSchedule ??
      (importedDurationFromAgenda > 0 ? importedDurationFromAgenda : DEFAULT_DURATION_MINUTES)
    const shouldScheduleForLater = Number.isFinite(importedStartMs) && importedStartMs > Date.now()
    const builderTimezone = getQuickStartOrganizerTimezone(user)
    const decodedTitle = decodeHtmlEntities(String(data?.title ?? ''))
    const agendaItems = sanitizeImportedAgendaForBuilder(mappedAgendaItems, decodedTitle, importedDurationMinutes)

    setForm({
      ...buildEmptyForm(user),
      meetingTypeId: data?.meetingTypeId ?? data?.meetingType ?? 'default',
      title: decodedTitle,
      audienceMode: data?.audienceMode === 'external' ? 'external' : 'internal',
      participants: (data?.participants ?? [])
        .map((participant) => normalizeParticipant(participant))
        .filter((participant) => participant.email && participant.email !== organizerEmailLower),
      selectedTeamIds: importedTeamIds,
      recurrenceKind: data?.isRecurring ? 'recurring' : 'one_off',
      recurrenceRule: {
        ...DEFAULT_RECURRENCE_RULE,
        ...(data?.recurrenceRule && typeof data.recurrenceRule === 'object' ? data.recurrenceRule : {}),
      },
      recurringSeriesId: null,
      scheduleMode: shouldScheduleForLater ? 'later' : 'now',
      scheduledAtLocal: shouldScheduleForLater ? formatDateTimeLocal(importedStartTime, builderTimezone) : '',
      durationMinutes: String(importedDurationMinutes),
      timezone: builderTimezone,
      agendaItems,
    })
    setTeamsTouched(false)
  }, [organizerEmailLower, user])

  const handleImportSelect = useCallback(async (sourceMeetingId) => {
    setImportingMeeting(true)
    try {
      const response = await meetingsAPI.get(sourceMeetingId)
      const data = response?.data ?? response
      if (!data) {
        throw new Error('Could not load the selected meeting.')
      }

      applyImportedMeetingData(data)

      const isExternal = data?.audienceMode === 'external'
      setInternalAgendaLoadError(null)
      if (isExternal) {
        try {
          const iaRes = await internalAgendaAPI.get(sourceMeetingId)
          const payload = iaRes?.data ?? iaRes
          const ia = payload?.internalAgenda ?? payload?.data?.internalAgenda
          setInternalGoals(ia ? mapApiInternalAgendaToGoals(ia) : [])
        } catch {
          setInternalGoals([])
        }
      } else {
        setInternalGoals([])
      }

      setStageIndex(REVIEW_STAGE_INDEX)
      toast.success(
        'Meeting imported',
        'Everything from your selection is filled in below. Review and create when you are ready.'
      )
    } catch (error) {
      toast.error('Import failed', error?.message || 'Could not load the meeting. Please try again.')
    } finally {
      setImportingMeeting(false)
      setImportSearch('')
    }
  }, [applyImportedMeetingData, toast])

  const ensureTeamMembersLoaded = useCallback(async (teamId) => {
    if (teamMembersCacheRef.current[teamId]) {
      return teamMembersCacheRef.current[teamId]
    }

    try {
      const response = await teamsAPI.get(teamId)
      const members = response?.data?.members ?? []
      teamMembersCacheRef.current[teamId] = members
      return members
    } catch {
      teamMembersCacheRef.current[teamId] = []
      return []
    }
  }, [])

  const handleTeamToggle = useCallback(async (teamId, checked) => {
    setTeamsTouched(true)
    setPeopleInviteError(null)

    if (checked) {
      const members = await ensureTeamMembersLoaded(teamId)
      setForm((prev) => ({
        ...prev,
        selectedTeamIds: [...prev.selectedTeamIds, teamId],
        participants: mergeParticipantsFromTeam(prev.participants, members, teamId, organizerEmailLower),
      }))
      return
    }

    setForm((prev) => ({
      ...prev,
      selectedTeamIds: prev.selectedTeamIds.filter((id) => id !== teamId),
      participants: removeParticipantsFromTeam(prev.participants, teamId),
    }))
  }, [ensureTeamMembersLoaded, organizerEmailLower])

  const handleAddParticipant = useCallback(() => {
    const normalizedEmail = normalizeParticipantEmail(participantDraft.email, userDomain)
    if (!normalizedEmail) {
      setParticipantDraftError('Add an email address for each participant.')
      return
    }
    if (!isValidEmail(normalizedEmail)) {
      setParticipantDraftError(INVALID_EMAIL_MESSAGE)
      return
    }
    if (normalizedEmail === organizerEmailLower) {
      setParticipantDraftError('You are already the organizer. Add guests only.')
      return
    }

    const trimmedDraftName = String(participantDraft.name ?? '').trim()
    const knownName = knownContactsMap.get(normalizedEmail.toLowerCase())
    const fromGuess = () => guessParticipantNameFromEmail(normalizedEmail)
    const participantName = trimmedDraftName || knownName || fromGuess()
    if (trimmedDraftName && user?.id) {
      authAPI
        .upsertKnownContact(normalizedEmail, participantName)
        .then(() => {
          setKnownContactsMap((prev) => new Map(prev).set(normalizedEmail.toLowerCase(), participantName))
        })
        .catch(() => {})
    }

    setForm((prev) => {
      const existingIndex = prev.participants.findIndex((participant) => participant.email === normalizedEmail)
      if (existingIndex >= 0) {
        const nextParticipants = [...prev.participants]
        nextParticipants[existingIndex] = {
          ...nextParticipants[existingIndex],
          name: participantName || nextParticipants[existingIndex].name,
          manual: true,
        }
        return {
          ...prev,
          participants: nextParticipants,
        }
      }

      return {
        ...prev,
        participants: [
          ...prev.participants,
          {
            id: buildId('participant'),
            name: participantName,
            email: normalizedEmail,
            role: 'participant',
            manual: true,
            sourceTeamIds: [],
          },
        ],
      }
    })
    setParticipantDraft({ name: '', email: '' })
    setParticipantDraftError(null)
    setPeopleInviteError(null)
  }, [knownContactsMap, organizerEmailLower, participantDraft.email, participantDraft.name, user?.id, userDomain])

  const handleParticipantKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      event.stopPropagation()
      handleAddParticipant()
    }
  }, [handleAddParticipant])

  const removeParticipant = useCallback((email) => {
    setForm((prev) => ({
      ...prev,
      participants: prev.participants.filter((participant) => participant.email !== email),
    }))
  }, [])

  const updateAgendaItem = useCallback((itemId, patch) => {
    setForm((prev) => ({
      ...prev,
      agendaItems: prev.agendaItems.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
    }))
    if (patch.title !== undefined && String(patch.title).trim()) {
      setAgendaTitleErrorItemIds((prev) => prev.filter((id) => id !== itemId))
    }
    if (patch.durationMinutes !== undefined) {
      const m = Number(patch.durationMinutes)
      if (Number.isFinite(m) && m > 0) {
        setAgendaDurationErrorItemIds((prev) => prev.filter((id) => id !== itemId))
      }
    }
    setAgendaTimeBudgetError(null)
  }, [])

  const clearInlineValidation = useCallback(() => {
    setTitleFieldError(null)
    setStepInlineError(null)
    setPeopleInviteError(null)
    setParticipantDraftError(null)
    setScheduleDurationError(null)
    setScheduleDateTimeError(null)
    setAgendaTitleErrorItemIds([])
    setAgendaDurationErrorItemIds([])
    setAgendaTimeBudgetError(null)
  }, [])

  const focusAgendaItemField = useCallback((itemId, selector) => {
    requestAnimationFrame(() => {
      scrollMbAgendaItemIntoView(itemId)
      const row = document.querySelector(mbAgendaItemSelector(itemId))
      const el = row?.querySelector(selector)
      if (el instanceof HTMLElement) {
        el.focus({ preventScroll: true })
      }
    })
  }, [])

  const handleStageValidationFailure = useCallback(
    (stageId, validationError) => {
      clearInlineValidation()

      if (stageId === 'title' && validationError === TITLE_EMPTY_ERROR) {
        setTitleFieldError(validationError)
        requestAnimationFrame(() => {
          meetingBuilderTitleRef.current?.focus({ preventScroll: true })
        })
        return true
      }

      if (stageId === 'people') {
        setPeopleInviteError(validationError)
        return true
      }

      if (stageId === 'schedule') {
        if (validationError.includes('when the meeting should happen')) {
          setStepInlineError(validationError)
          return true
        }
        if (validationError.includes('duration')) {
          setScheduleDurationError(validationError)
          return true
        }
        if (validationError.includes('date and time') || validationError.includes('valid date')) {
          setScheduleDateTimeError(validationError)
          return true
        }
      }

      if (stageId === 'agenda') {
        if (isAgendaInlineTitleValidationError(validationError)) {
          const ids = agendaItemsMissingTitleIds(form.agendaItems)
          setAgendaTitleErrorItemIds(ids)
          if (ids[0]) {
            focusAgendaItemField(ids[0], '[data-mb-agenda-title-input]')
          }
          return true
        }
        if (validationError === AGENDA_DURATION_ERROR) {
          const ids = agendaItemsMissingDurationIds(form.agendaItems)
          setAgendaDurationErrorItemIds(ids)
          if (ids[0]) {
            focusAgendaItemField(ids[0], 'input[type="number"]')
          }
          return true
        }
        if (isAgendaTimeBudgetError(validationError)) {
          setAgendaTimeBudgetError(validationError)
          return true
        }
      }

      setStepInlineError(validationError)
      return true
    },
    [clearInlineValidation, focusAgendaItemField, form.agendaItems]
  )

  const addAgendaItem = useCallback(() => {
    const newItem = buildEmptyAgendaItem()
    pendingScrollMbAgendaItemIdRef.current = newItem.id
    setForm((prev) => ({
      ...prev,
      agendaItems: [...prev.agendaItems, newItem],
    }))
    setAgendaTimeBudgetError(null)
  }, [])

  const removeAgendaItem = useCallback((itemId) => {
    setForm((prev) => {
      const nextItems = prev.agendaItems.filter((item) => item.id !== itemId)
      return {
        ...prev,
        agendaItems: nextItems.length > 0 ? nextItems : [buildEmptyAgendaItem()],
      }
    })
    setAgendaTitleErrorItemIds((prev) => prev.filter((id) => id !== itemId))
    setAgendaDurationErrorItemIds((prev) => prev.filter((id) => id !== itemId))
    setInternalGoals((prev) => prev.filter((g) => g.linkedPublicAgendaItemId !== itemId))
  }, [])

  const persistInternalAgendaForMeeting = useCallback(
    async (mid) => {
      const body =
        form.audienceMode === 'external'
          ? buildInternalAgendaStateFromGoals(internalGoals, { authorUserId: user?.id ?? null })
          : emptyInternalAgendaState()
      await internalAgendaAPI.put(String(mid), body)
    },
    [form.audienceMode, internalGoals, user?.id]
  )

  const addInternalGoalForAgendaItem = useCallback((agendaItemId) => {
    setInternalGoals((prev) => {
      if (prev.some((g) => g.linkedPublicAgendaItemId === agendaItemId)) return prev
      return [
        ...prev,
        {
          internalAgendaItemId: newInternalGoalId(),
          linkedPublicAgendaItemId: agendaItemId,
          desiredOutcome: 'gather_information',
          privateNotes: '',
          visibilityScope: 'shared_internal_team',
          authorUserId: null,
        },
      ]
    })
  }, [])

  const updateInternalGoalRow = useCallback((internalAgendaItemId, patch) => {
    setInternalGoals((prev) =>
      prev.map((g) => (g.internalAgendaItemId === internalAgendaItemId ? { ...g, ...patch } : g))
    )
  }, [])

  const removeInternalGoalRow = useCallback((internalAgendaItemId) => {
    setInternalGoals((prev) => prev.filter((g) => g.internalAgendaItemId !== internalAgendaItemId))
  }, [])

  const resolveSchedulingFields = useCallback(
    (options = {}) => resolveSchedulingFieldsForForm(form, user, options),
    [form, user]
  )

  const validateStage = useCallback(
    (stageId, options = {}) => validateStageWithFormState(stageId, form, { user, forDraft: options.forDraft === true }),
    [form, user]
  )

  const goNext = useCallback(() => {
    if (currentStage === 'start') {
      setStepInlineError('Use the options above to continue.')
      return
    }
    const validationError = validateStage(currentStage)
    if (validationError) {
      handleStageValidationFailure(currentStage, validationError)
      return
    }
    clearInlineValidation()
    setStageIndex((prev) => Math.min(prev + 1, BUILDER_STAGES.length - 1))
  }, [clearInlineValidation, currentStage, handleStageValidationFailure, validateStage])

  const advanceWithFormPatch = useCallback(
    (patch) => {
      let nextForm
      flushSync(() => {
        setForm((prev) => {
          nextForm = { ...prev, ...patch }
          return nextForm
        })
        clearInlineValidation()
      })
      const validationError = validateStageWithFormState(currentStage, nextForm, { user, forDraft: false })
      if (validationError) {
        handleStageValidationFailure(currentStage, validationError)
        return
      }
      clearInlineValidation()
      setStageIndex((prev) => Math.min(prev + 1, BUILDER_STAGES.length - 1))
    },
    [clearInlineValidation, currentStage, handleStageValidationFailure, user]
  )

  const advanceScheduleWithDurationPreset = useCallback(
    (minutes) => {
      let nextForm
      flushSync(() => {
        setForm((prev) => {
          nextForm = { ...prev, durationMinutes: String(minutes) }
          return nextForm
        })
        clearInlineValidation()
      })
      const validationError = validateStageWithFormState('schedule', nextForm, { user, forDraft: false })
      if (validationError) {
        handleStageValidationFailure('schedule', validationError)
        return
      }
      clearInlineValidation()
      setStageIndex((prev) => Math.min(prev + 1, BUILDER_STAGES.length - 1))
    },
    [clearInlineValidation, handleStageValidationFailure, user]
  )

  const goBack = useCallback(() => {
    // Opened from the meeting page's Edit button and sitting on the review step:
    // "Back" should leave the builder and return to that meeting, not step back
    // through the wizard the user never intended to walk.
    if (arrivedFromEdit && stageIndex === REVIEW_STAGE_INDEX && meetingId) {
      navigate(`/meeting/${meetingId}`)
      return
    }
    clearInlineValidation()
    setStageIndex((prev) => Math.max(prev - 1, minimumStageIndex))
  }, [arrivedFromEdit, clearInlineValidation, meetingId, minimumStageIndex, navigate, stageIndex])

  const tryBlurAdvanceFromStep = useCallback(
    (stageId, e) => {
      if (successState) return
      if (e.currentTarget.contains(e.relatedTarget)) return
      const rt = e.relatedTarget
      if (rt && rt instanceof Element && rt.closest?.('[data-mb-no-blur-advance]')) {
        return
      }
      const err = validateStageWithFormState(stageId, form, { user, forDraft: false })
      if (err) {
        handleStageValidationFailure(stageId, err)
        return
      }
      clearInlineValidation()
      setStageIndex((prev) => Math.min(prev + 1, BUILDER_STAGES.length - 1))
    },
    [clearInlineValidation, form, handleStageValidationFailure, successState, user]
  )

  const buildCommonPayload = useCallback((options = {}) => {
    const { forDraft = false } = options
    const { scheduledStartTime, scheduledEndTime, effectiveDuration } = resolveSchedulingFields({ forDraft })
    const draftAgendaItems = cleanAgendaItems.filter((item) => item.title)
    const agenda = forDraft
      ? (draftAgendaItems.length > 0 ? buildAgendaPayload(draftAgendaItems, effectiveDuration) : [])
      : buildAgendaPayload(form.agendaItems, effectiveDuration)
    const recurrenceRule = buildRecurrenceRulePayload(form.recurrenceKind, form.recurrenceRule)

    return {
      title: form.title.trim(),
      meetingType: form.meetingTypeId || 'default',
      meetingTypeId: form.meetingTypeId || 'default',
      agenda,
      participants: form.participants.map((participant) => ({
        id: participant.id,
        name: participant.name || undefined,
        displayName: participant.name || undefined,
        email: participant.email,
      })),
      governanceConfig: QUICK_START_DEFAULT_GOVERNANCE_CONFIG,
      audienceMode: form.audienceMode === 'external' ? 'external' : 'internal',
      scheduledStartTime,
      scheduledEndTime,
      isRecurring: form.recurrenceKind === 'recurring',
      recurringSeriesId:
        form.recurrenceKind === 'recurring'
          ? (form.recurringSeriesId ?? (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : buildId('series')))
          : undefined,
      recurrenceRule,
      organizerTimezone: (form.timezone || getQuickStartOrganizerTimezone(user)) || undefined,
      creationSource: 'agenda_builder',
      ...(form.selectedTeamIds.length > 0 || teamsTouched ? { teamIds: form.selectedTeamIds } : {}),
    }
  }, [cleanAgendaItems, form.agendaItems, form.audienceMode, form.meetingTypeId, form.participants, form.recurrenceKind, form.recurrenceRule, form.recurringSeriesId, form.selectedTeamIds, form.timezone, form.title, resolveSchedulingFields, teamsTouched, user])

  const ensureServerMeetingForAttachments = useCallback(async () => {
    if (existingMeeting?.meetingId && !existingMeeting.isLocalOnly) {
      return existingMeeting.meetingId
    }
    if (!form.title.trim()) {
      return null
    }
    if (ensureAttachmentMeetingInFlightRef.current) {
      return null
    }
    ensureAttachmentMeetingInFlightRef.current = true
    setAttachmentMeetingProvisioning(true)
    try {
      const payload = {
        ...buildCommonPayload({ forDraft: true }),
        isDraft: true,
        createWithZoom: false,
      }
      let savedId =
        existingMeeting?.meetingId && !existingMeeting.isLocalOnly ? existingMeeting.meetingId : null
      if (savedId) {
        await meetingsAPI.update(savedId, payload)
      } else {
        const response = await meetingsAPI.create(payload)
        savedId = response?.data?.meetingId ?? response?.meetingId ?? null
      }
      if (savedId) {
        setExistingMeeting({
          meetingId: savedId,
          isDraft: true,
          isLocalOnly: false,
          recurringSeriesId: form.recurringSeriesId ?? null,
        })
        if (meetingId && meetingId !== savedId) {
          removeDraftMeeting(meetingId)
        }
      }
      return savedId
    } catch (error) {
      toast.error(
        'Could not prepare uploads',
        error?.message || 'Save a draft meeting before attaching documents.'
      )
      return null
    } finally {
      ensureAttachmentMeetingInFlightRef.current = false
      setAttachmentMeetingProvisioning(false)
    }
  }, [
    buildCommonPayload,
    existingMeeting?.isLocalOnly,
    existingMeeting?.meetingId,
    form.recurringSeriesId,
    form.title,
    meetingId,
    toast,
  ])

  useEffect(() => {
    if ((currentStage !== 'agenda' && currentStage !== 'review') || successState) return
    if (attachmentMeetingId) return
    if (!form.title.trim()) return
    void ensureServerMeetingForAttachments()
  }, [
    attachmentMeetingId,
    currentStage,
    ensureServerMeetingForAttachments,
    form.title,
    successState,
  ])

  useEffect(() => {
    if (currentStage !== 'review' || reviewEditing) return
    setAttachmentsSummaryVersion((v) => v + 1)
  }, [currentStage, reviewEditing])

  const handleSaveDraft = useCallback(async () => {
    if (existingMeeting && existingMeeting.isDraft === false) {
      toast.error('Draft not available', 'Existing scheduled meetings cannot be saved back as drafts.')
      return
    }
    if (!form.title.trim()) {
      setStageIndex(BUILDER_STAGES.findIndex((stage) => stage.id === 'title'))
      setTitleFieldError('Add a title before saving a draft.')
      requestAnimationFrame(() => {
        meetingBuilderTitleRef.current?.focus({ preventScroll: true })
      })
      return
    }

    setSavingDraft(true)
    clearInlineValidation()

    try {
      const payload = {
        ...buildCommonPayload({ forDraft: true }),
        isDraft: true,
        createWithZoom: false,
      }

      let savedId = existingMeeting?.meetingId ?? null
      if (existingMeeting?.meetingId && !existingMeeting.isLocalOnly) {
        await meetingsAPI.update(existingMeeting.meetingId, payload)
        savedId = existingMeeting.meetingId
      } else {
        const response = await meetingsAPI.create(payload)
        const savedMeetingId = response?.data?.meetingId ?? response?.meetingId
        savedId = savedMeetingId ?? null
        if (meetingId && savedMeetingId) {
          removeDraftMeeting(meetingId)
        }
      }

      if (savedId) {
        try {
          await persistInternalAgendaForMeeting(savedId)
        } catch (e) {
          toast.error(
            'Partial save',
            e instanceof Error ? e.message : 'Draft saved, but internal goals could not be saved. Try again from the meeting page.'
          )
        }
      }

      requestOnboardingVideosCheck()
      if (meetingId) {
        removeDraftMeeting(meetingId)
      }
      toast.success('Draft saved', 'You can finish it from the dashboard whenever you are ready.')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      toast.error('Draft save failed', error?.message || 'Could not save the draft. Please try again.')
    } finally {
      setSavingDraft(false)
    }
  }, [buildCommonPayload, clearInlineValidation, existingMeeting, form.title, meetingId, navigate, persistInternalAgendaForMeeting, toast])

  const handleDeleteDraft = useCallback(async () => {
    setDeletingDraft(true)
    try {
      const serverMeetingId =
        existingMeeting?.meetingId && !existingMeeting.isLocalOnly ? existingMeeting.meetingId : null
      if (serverMeetingId) {
        await meetingsAPI.delete(serverMeetingId)
      }
      if (meetingId) {
        removeDraftMeeting(meetingId)
      } else if (existingMeeting?.isLocalOnly && existingMeeting.meetingId) {
        removeDraftMeeting(existingMeeting.meetingId)
      }
      setShowDeleteDraftConfirm(false)
      toast.success('Draft deleted', 'The draft has been removed.')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      toast.error('Delete failed', error?.message || 'Could not delete the draft. Please try again.')
    } finally {
      setDeletingDraft(false)
    }
  }, [existingMeeting, meetingId, navigate, toast])

  const handleSubmit = useCallback(async () => {
    for (const stage of BUILDER_STAGES) {
      const validationError = validateStage(stage.id)
      if (validationError) {
        setStageIndex(BUILDER_STAGES.findIndex((item) => item.id === stage.id))
        if (handleStageValidationFailure(stage.id, validationError)) return
        return
      }
    }

    if (!zoomConnected) {
      setStepInlineError('Connect Zoom before creating a meeting. You can still save this as a draft.')
      return
    }

    setSubmitting(true)
    clearInlineValidation()

    try {
      const payload = {
        ...buildCommonPayload(),
        isDraft: false,
        createWithZoom: true,
        enableRecallBot: true,
        recallTranscriptionOnly: true,
      }

      if (existingMeeting?.meetingId && existingMeeting.isDraft === false && !existingMeeting.isLocalOnly) {
        await meetingsAPI.update(existingMeeting.meetingId, payload)
        try {
          await persistInternalAgendaForMeeting(existingMeeting.meetingId)
        } catch (e) {
          toast.error(
            'Partial save',
            e instanceof Error
              ? e.message
              : 'Meeting updated, but internal goals could not be saved. Open the meeting to retry.'
          )
        }
        requestOnboardingVideosCheck()
        toast.success('Meeting updated', 'Your meeting changes are saved.')
        navigate(`/meeting/${existingMeeting.meetingId}`, { replace: true })
        return
      }

      const response =
        existingMeeting?.meetingId && existingMeeting.isDraft === true && !existingMeeting.isLocalOnly
          ? await (async () => {
            await meetingsAPI.update(existingMeeting.meetingId, {
              ...payload,
              isDraft: true,
              createWithZoom: false,
            })
            return meetingsAPI.convertDraftToLive(existingMeeting.meetingId, {
              enableRecallBot: true,
              recallTranscriptionOnly: true,
              organizerTimezone: payload.organizerTimezone,
            })
          })()
          : await meetingsAPI.create(payload)

      const normalized = normalizeQuickStartMeetingResponse(response)
      const resolvedMeetingId = normalized.meetingId || existingMeeting?.meetingId || null
      if (resolvedMeetingId) {
        try {
          await persistInternalAgendaForMeeting(resolvedMeetingId)
        } catch (e) {
          toast.error(
            'Partial save',
            e instanceof Error
              ? e.message
              : 'Meeting was created, but internal goals could not be saved. Edit the meeting to add them.'
          )
        }
      }
      const successPayload = {
        mode: determineSuccessMode(payload.scheduledStartTime),
        meetingId: resolvedMeetingId ?? normalized.meetingId,
        joinUrl: normalized.joinUrl,
        password: normalized.password,
        title: form.title.trim(),
        scheduledStartTime: payload.scheduledStartTime,
        scheduledEndTime: payload.scheduledEndTime,
        isRecurring: payload.isRecurring === true,
        createdWithUserZoom: normalized.createdWithUserZoom,
        addBotAfterYouJoin: normalized.addBotAfterYouJoin,
      }

      if (meetingId) {
        removeDraftMeeting(meetingId)
      }
      requestOnboardingVideosCheck()
      toast.success(
        'Meeting Created!',
        successPayload.mode === 'scheduled'
          ? 'Your scheduled meeting is ready.'
          : 'Your meeting is ready to join.'
      )
      const coaching = await fetchBuilderCompletionCoaching({
        meetingTitle: form.title.trim(),
        agendaItems: cleanAgendaItems.filter((item) => item.title),
        meetingDurationMinutes: meetingDuration,
        seed: successPayload.meetingId,
      })
      setSuccessState({ ...successPayload, ...coaching })
    } catch (error) {
      const message = error?.message || 'Failed to create the meeting. Please try again.'
      if (message.includes('zoom_reauth_required') || message.includes('Zoom connection has expired')) {
        toast.error('Zoom Disconnected', 'Your Zoom connection has expired. Reconnecting...')
        if (connectZoomUrl) {
          window.setTimeout(() => {
            window.location.href = connectZoomUrl
          }, 1500)
        }
      } else {
        toast.error('Creation Failed', message)
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    buildCommonPayload,
    cleanAgendaItems,
    clearInlineValidation,
    connectZoomUrl,
    existingMeeting,
    form.title,
    meetingDuration,
    meetingId,
    navigate,
    persistInternalAgendaForMeeting,
    toast,
    validateStage,
    handleStageValidationFailure,
    zoomConnected,
  ])

  const handleRequestSendUpdate = useCallback(() => {
    for (const stage of BUILDER_STAGES) {
      const validationError = validateStage(stage.id)
      if (validationError) {
        setStageIndex(BUILDER_STAGES.findIndex((item) => item.id === stage.id))
        if (handleStageValidationFailure(stage.id, validationError)) return
        return
      }
    }
    if (!existingMeeting?.meetingId || existingMeeting.isDraft !== false || existingMeeting.isLocalOnly) {
      return
    }
    setShowSendUpdateConfirm(true)
  }, [existingMeeting, handleStageValidationFailure, validateStage])

  const handleConfirmSendUpdate = useCallback(async () => {
    if (!existingMeeting?.meetingId) return

    setShowSendUpdateConfirm(false)
    setSendingUpdate(true)
    clearInlineValidation()

    try {
      const payload = {
        ...buildCommonPayload(),
        isDraft: false,
      }

      await meetingsAPI.sendUpdate(existingMeeting.meetingId, payload)

      try {
        await persistInternalAgendaForMeeting(existingMeeting.meetingId)
      } catch (e) {
        toast.error(
          'Partial save',
          e instanceof Error
            ? e.message
            : 'Meeting updated, but internal goals could not be saved. Open the meeting to retry.'
        )
      }
      requestOnboardingVideosCheck()
      toast.success('Update sent', 'Meeting updated and participants have been notified.')
      navigate(`/meeting/${existingMeeting.meetingId}`, { replace: true })
    } catch (error) {
      const message = error?.message || 'Failed to save and send update. Please try again.'
      toast.error('Send update failed', message)
    } finally {
      setSendingUpdate(false)
    }
  }, [
    buildCommonPayload,
    clearInlineValidation,
    existingMeeting,
    navigate,
    persistInternalAgendaForMeeting,
    toast,
  ])

  useEffect(() => {
    const isSkippableInput = (t) => {
      if (t instanceof HTMLInputElement) {
        const it = t.type
        if (it === 'checkbox' || it === 'radio' || it === 'file' || it === 'hidden' || it === 'button' || it === 'reset') {
          return true
        }
      }
      return false
    }

    const onKeyDown = (event) => {
      if (event.key !== 'Enter' || event.repeat) return
      if (event.isComposing) return
      if (successState) return
      if (event.defaultPrevented) return

      const t = event.target
      if (!(t instanceof HTMLElement)) return
      if (t.closest('dialog, [role="dialog"]')) return
      if (t.isContentEditable) return
      if (t.tagName === 'TEXTAREA') return
      if (t.tagName === 'SELECT') return
      if (t.tagName === 'BUTTON' || t.tagName === 'A') return
      if (isSkippableInput(t)) return

      if (currentStage === 'review') {
        if (submitting || savingDraft || sendingUpdate) return
        event.preventDefault()
        void handleSubmit()
        return
      }

      const commitGroup = getMbEnterCommitGroup(t)
      if (commitGroup) {
        if (commitGroup === 'mb-title-step') {
          event.preventDefault()
          goNext()
          return
        }
        if (commitGroup === 'mb-schedule-datetime') {
          if (enterCommitPrimedKeyRef.current === commitGroup) {
            event.preventDefault()
            goNext()
          } else {
            enterCommitPrimedKeyRef.current = commitGroup
          }
          return
        }
        if (enterCommitPrimedKeyRef.current === commitGroup) {
          event.preventDefault()
          goNext()
        } else {
          enterCommitPrimedKeyRef.current = commitGroup
        }
        return
      }

      /** Title step: Enter advances even when focus is not on `#meeting-builder-title` (e.g. after Stage 2, focus landed on `<body>`). */
      if (currentStage === 'title') {
        event.preventDefault()
        goNext()
        return
      }

      enterCommitPrimedKeyRef.current = null
    }

    window.addEventListener('keydown', onKeyDown, false)
    return () => window.removeEventListener('keydown', onKeyDown, false)
  }, [currentStage, goNext, handleSubmit, savingDraft, sendingUpdate, submitting, successState])

  const handleJoinNow = useCallback(async () => {
    if (!successState?.joinUrl) return

    reserveJoinWindow()
    setJoining(true)

    let effectiveMeetingId = successState.meetingId
    let joinUrl = successState.joinUrl
    let password = successState.password
    let createdChildOccurrence = false

    const revertStartingStatus = async () => {
      try {
        await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'created' })
      } catch {
        // Best-effort only.
      }
    }

    try {
      if (successState.isRecurring) {
        const nextOccurrence = await meetingsAPI.startNextOccurrence(successState.meetingId)
        effectiveMeetingId = nextOccurrence?.data?.meetingId ?? nextOccurrence?.meetingId ?? successState.meetingId
        joinUrl = nextOccurrence?.data?.joinUrl ?? nextOccurrence?.joinUrl ?? joinUrl
        password = nextOccurrence?.data?.password ?? nextOccurrence?.password ?? password
        createdChildOccurrence = effectiveMeetingId !== successState.meetingId
      }

      await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'starting' })

      const callStart = (force = false) =>
        zoomAPI.startExistingMeetingForUser({
          meetingId: effectiveMeetingId,
          password: password ?? undefined,
          force,
        })

      try {
        await callStart(false)
      } catch (firstError) {
        if (firstError?.status === 409 && firstError?.code === 'CONCURRENT_ZOOM_CALL') {
          const confirmed = window.confirm(
            'You are currently in another Zoom meeting. Starting this meeting will end the other call. Continue?'
          )
          if (!confirmed) {
            if (createdChildOccurrence) {
              try {
                await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
              } catch {
                // Best effort only.
              }
            }
            await revertStartingStatus()
            closePendingJoinWindow()
            setJoining(false)
            return
          }
          await callStart(true)
        } else if (firstError?.status === 409 && firstError?.code === 'CONCURRENT_ZOOM_CALL_DETECTED') {
          if (createdChildOccurrence) {
            try {
              await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
            } catch {
              // Best effort only.
            }
          }
          await revertStartingStatus()
          closePendingJoinWindow()
          toast.error('Already in another Zoom call', 'Please leave your other Zoom meeting and try again.')
          setJoining(false)
          return
        } else {
          throw firstError
        }
      }

      const pendingWindow = takePendingJoinWindow()
      if (pendingWindow) {
        pendingWindow.location = joinUrl
      } else {
        window.open(joinUrl, '_blank')
      }

      navigate(`/meeting/${effectiveMeetingId}`, {
        replace: true,
        state: {
          joinUrl,
          password,
          createdWithUserZoom: successState.createdWithUserZoom,
          cppBotJoined: true,
          addBotAfterYouJoin: successState.addBotAfterYouJoin,
        },
      })
    } catch (error) {
      if (createdChildOccurrence) {
        try {
          await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
        } catch {
          // Best effort only.
        }
      }
      await revertStartingStatus()
      closePendingJoinWindow()
      toast.error(error?.message || 'Could not start the meeting. Please try again.')
    } finally {
      setJoining(false)
    }
  }, [closePendingJoinWindow, navigate, reserveJoinWindow, successState, takePendingJoinWindow, toast])


  if (initializing || loadingCatalog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="lg:ml-[280px] p-4 pt-20 lg:p-8 lg:pt-8">
          <div className="glass rounded-[32px] p-8 sm:p-10">
            <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#16c1b2]" />
              <h1 className="mt-6 text-2xl font-bold text-gray-900">Loading meeting builder</h1>
              <p className="mt-2 max-w-md text-gray-500">
                Pulling your meeting types, teams, and meeting details into the new builder.
              </p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <MeetingSetupOverlay
        visible={submitting}
        title="Setting Up Rithmo"
        subtitle="Creating your meeting..."
      />
      <MeetingSetupOverlay
        visible={!submitting && savingDraft}
        title="Saving Draft"
        subtitle="Keeping your progress safe..."
      />
      <MeetingSetupOverlay
        visible={!submitting && !savingDraft && joining}
        title="Setting Up Rithmo"
        subtitle="Starting your meeting..."
      />
      <MeetingSetupOverlay
        visible={!submitting && !savingDraft && !joining && importingMeeting}
        title="Importing Meeting"
        subtitle="Loading the selected meeting..."
      />

      <main className="lg:ml-[280px] relative overflow-hidden p-4 pt-20 lg:p-8 lg:pt-8">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,rgba(22,193,178,0.12),transparent_62%)]" />
        <div className="pointer-events-none absolute right-0 top-20 h-64 w-64 rounded-full bg-[#e4faf8] blur-3xl opacity-60" />

        <div className="relative mx-auto max-w-5xl">
          <div className="mb-6">
            <Link
              to={arrivedFromEdit && meetingId ? `/meeting/${meetingId}` : '/dashboard'}
              data-mb-no-blur-advance
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              {arrivedFromEdit && meetingId ? 'Back to meeting' : 'Dashboard'}
            </Link>
          </div>

          <div className="glass rounded-[32px] p-6 sm:p-8 lg:p-10">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-4 sm:gap-3">
              <button
                type="button"
                onClick={goBack}
                data-mb-no-blur-advance
                disabled={stageIndex === minimumStageIndex || Boolean(successState)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                  stageIndex > minimumStageIndex && !successState
                    ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              {!zoomConnected && !successState ? (
                <div
                  className="order-last flex w-full min-w-0 basis-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs leading-snug text-amber-800 sm:order-none sm:basis-auto sm:flex-1 sm:px-3"
                  role="status"
                >
                  <VideoIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
                  <span className="min-w-0">
                    Zoom is required to create the final meeting. You can still save a draft.
                  </span>
                </div>
              ) : null}

              {!successState && (
                <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
                  {existingMeeting?.isDraft !== false && (
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      data-mb-no-blur-advance
                      disabled={savingDraft || submitting || joining}
                      className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Save Draft
                    </button>
                  )}
                  {!zoomConnected && (
                    <a
                      href={connectZoomUrl || '#'}
                      data-mb-no-blur-advance
                      onClick={(event) => {
                        if (!connectZoomUrl) {
                          event.preventDefault()
                        }
                      }}
                      className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4"
                    >
                      <VideoIcon className="w-4 h-4" />
                      Connect Zoom
                    </a>
                  )}
                </div>
              )}
            </div>

            {!successState && (
              <div className="mt-4">
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200/90"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(
                    ((stageIndex + 1) / Math.max(1, BUILDER_STAGES.length)) * 100
                  )}
                  aria-label="Progress in meeting builder"
                >
                  <div
                    className="h-full rounded-full transition-[width] duration-300 ease-out"
                    style={{
                      background: 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)',
                      width: `${((stageIndex + 1) / Math.max(1, BUILDER_STAGES.length)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <div
              ref={builderStepBodyRef}
              className="min-h-0"
              style={
                successState
                  ? undefined
                  : {
                      marginTop: stepBodyOffsetPx,
                      maxHeight: stepBodyMaxHeightPx ? stepBodyMaxHeightPx : undefined,
                      overflowY: stepBodyOverflows ? 'auto' : undefined,
                    }
              }
            >
              {!successState ? (
                <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-[#16c1b2]">
                  {`Step ${stageIndex + 1}`}
                </p>
              ) : null}
            <AnimatePresence mode="wait">
              {successState ? (
                <StepShell
                  eyebrow="Complete"
                  title={
                    successState.coachingTitle ??
                    (successState.mode === 'scheduled' ? 'Meeting Created' : 'Your meeting is ready')
                  }
                  description={
                    successState.coachingHelper ??
                    (successState.mode === 'scheduled'
                      ? 'You can see your scheduled meeting on the main Dashboard or Calendar links.'
                      : 'Your meeting is set up. Join when you are ready.')
                  }
                >
                  <div className="mx-auto w-full max-w-2xl rounded-[28px] border border-[#16c1b2]/30 bg-[#f0fdfc]/80 p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#16c1b2] text-white">
                        <CheckCircle2 className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-semibold text-gray-900">{successState.title || 'Meeting Created'}</p>
                        <p className="mt-1 text-sm text-[#0d8f84]">
                          {successState.scheduledStartTime
                            ? formatInTimezone(successState.scheduledStartTime, form.timezone, {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                            : 'Starts now'}
                        </p>
                        {successState.mode === 'scheduled' ? (
                          <p className="mt-4 text-sm text-[#0d8f84]">
                            Redirecting to the dashboard in 30 seconds, or close this screen anytime.
                          </p>
                        ) : (
                          <p className="mt-4 text-sm text-[#0d8f84]">
                            Rithmo will start the Zoom-backed meeting when you click Join Now.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                      {successState.mode === 'join-now' ? (
                        <button
                          type="button"
                          onClick={handleJoinNow}
                          disabled={joining}
                          className="btn-primary inline-flex items-center gap-2"
                        >
                          {joining ? (
                            <>
                              <Loader2 className="h-5 w-5 animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              <VideoIcon className="h-5 w-5" />
                              Join Now
                            </>
                          )}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => navigate('/dashboard', { replace: true })}
                        className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                      >
                        Close Builder
                      </button>
                    </div>
                  </div>
                </StepShell>
              ) : null}

              {!successState && currentStage === 'start' ? (
                <StepShell title="How do you want to start?">
                  {/* Platform picker — beta only. Zoom is the builder's native
                      host flow; Teams/Meet hand off to the assistant join flow. */}
                  {isExternalJoinEnabled(user?.email) ? (
                    <div className="mx-auto mb-8 w-full max-w-2xl">
                      <p className="mb-3 text-center text-sm font-medium text-gray-600">
                        Which platform is this meeting on?
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {MEETING_PLATFORMS.map((platform) => {
                          const isSelected = !platform.external
                          return (
                            <button
                              key={platform.key}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => {
                                if (platform.external) navigate('/join-external')
                              }}
                              className={`group relative flex flex-col items-center gap-2 rounded-2xl border p-4 text-center transition ${
                                isSelected
                                  ? 'border-[#16c1b2] bg-[#f0fdfc] ring-2 ring-[#16c1b2]/30'
                                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow-sm">
                                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                                  <path d={platform.logoPath} fill={platform.brandColor} />
                                </svg>
                              </span>
                              <span className="text-sm font-semibold text-gray-900">{platform.label}</span>
                              <span className={`text-xs font-medium ${isSelected ? 'text-[#0d8f84]' : 'text-gray-400'}`}>
                                {isSelected ? 'Selected' : 'Add Rithmo to a link'}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}

                  <div className="mx-auto grid w-full max-w-2xl gap-3 sm:grid-cols-2 sm:gap-4">
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      iconAccentBackground
                      icon={<Plus className="h-5 w-5" />}
                      title="Start fresh"
                      onClick={() => {
                        setStartMode('scratch')
                        setStepInlineError(null)
                        setStageIndex(1)
                      }}
                    />
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      iconAccentBackground
                      icon={<Copy className="h-5 w-5" />}
                      title="Import existing meeting"
                      onClick={() => {
                        setStartMode('import')
                        setStepInlineError(null)
                      }}
                    />
                  </div>

                  {startMode === 'import' ? (
                    <div className="mt-8 w-full rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Search meetings to import</span>
                        <input
                          type="text"
                          value={importSearch}
                          onChange={(event) => setImportSearch(event.target.value)}
                          placeholder="Search by meeting title"
                          data-mb-enter-group="mb-start-import-search"
                          className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                        />
                      </label>

                      <div className="mt-5 rounded-2xl border border-gray-100 bg-gray-50">
                        {loadingImportList ? (
                          <div className="flex items-center gap-3 px-4 py-4 text-sm text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin text-[#16c1b2]" />
                            Searching meetings...
                          </div>
                        ) : importResults.length > 0 ? (
                          importResults.map((meeting) => (
                            <button
                              key={meeting.meetingId}
                              type="button"
                              onClick={() => {
                                void handleImportSelect(meeting.meetingId)
                              }}
                              className="flex w-full items-center justify-between gap-4 border-b border-gray-100 px-4 py-4 text-left transition last:border-b-0 hover:bg-white"
                            >
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold text-gray-900">
                                  {decodeHtmlEntities(String(meeting.title ?? 'Untitled meeting'))}
                                </span>
                                <span className="mt-1 block text-sm text-gray-500">
                                  {meeting.meetingId}
                                </span>
                              </span>
                              <span className="rounded-full border border-[#16c1b2]/30 bg-[#f0fdfc] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0d8f84]">
                                Import
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-4 text-sm text-gray-500">
                            No meetings found. Try another title or continue with a blank meeting.
                          </div>
                        )}
                      </div>

                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={() => setStageIndex(1)}
                          className="text-sm font-medium text-gray-600 underline transition hover:text-gray-800"
                        >
                          Skip import
                        </button>
                      </div>
                    </div>
                  ) : null}
                  <BuilderStepInlineError message={stepInlineError} className="mt-4" />
                </StepShell>
              ) : null}

              {!successState && currentStage === 'meeting-type' ? (
                <StepShell title="What kind of meeting is this?" contentTopClassName="mt-6 sm:mt-7">
                  <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-3">
                    {meetingTypes.map((meetingType) => (
                      <button
                        type="button"
                        key={meetingType.id}
                        onClick={() => {
                          advanceWithFormPatch({ meetingTypeId: meetingType.id })
                        }}
                        className={builderMeetingTypeTileClassName()}
                      >
                        {meetingType.name}
                      </button>
                    ))}
                  </div>
                  <BuilderStepInlineError message={stepInlineError} className="mt-4" />
                </StepShell>
              ) : null}

              {!successState && currentStage === 'title' ? (
                <StepShell
                  title="What should we call the meeting?"
                  contentTopClassName="mt-6 sm:mt-7"
                >
                  <div className="mx-auto w-full max-w-2xl">
                    <label className="sr-only" htmlFor="meeting-builder-title">Meeting title</label>
                    <input
                      ref={meetingBuilderTitleRef}
                      id="meeting-builder-title"
                      type="text"
                      value={form.title}
                      onChange={(event) => setField('title', event.target.value)}
                      onBlur={(e) => tryBlurAdvanceFromStep('title', e)}
                      placeholder="Customer roadmap review"
                      data-mb-enter-group="mb-title-step"
                      aria-invalid={Boolean(titleFieldError)}
                      className={builderInputClassName(Boolean(titleFieldError), 'large')}
                    />
                    <BuilderStepInlineError message={titleFieldError} />
                    {String(form.title ?? '').trim() ? (
                      <div className="mt-3">
                        <MeetingInputQualityIndicator
                          label="Title quality"
                          tier={titleInputQuality.tier}
                          hint={INPUT_QUALITY_HINTS[titleInputQuality.tier]}
                        />
                      </div>
                    ) : null}
                    <p className="mt-2.5 text-sm text-gray-500">Press Enter when finished.</p>
                  </div>
                </StepShell>
              ) : null}

              {!successState && currentStage === 'audience' ? (
                <StepShell
                  title="Who is this meeting for?"
                >
                  <div className="mx-auto grid w-full max-w-2xl gap-4 md:grid-cols-2">
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      icon={<Users className="h-5 w-5" />}
                      title="Internal meeting"
                      onClick={() => {
                        advanceWithFormPatch({ audienceMode: 'internal' })
                      }}
                    />
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      icon={<Mail className="h-5 w-5" />}
                      title="External-facing meeting"
                      tooltip="Choosing External facing meeting allows you to append internal goals to a public facing agenda."
                      onClick={() => {
                        advanceWithFormPatch({ audienceMode: 'external' })
                      }}
                    />
                  </div>
                  <BuilderStepInlineError message={stepInlineError} className="mt-4" />
                </StepShell>
              ) : null}

              {!successState && currentStage === 'people' ? (
                <StepShell
                  title="Who should be invited?"
                >
                  <div className="w-full space-y-6">
                    {teamsList.length > 0 ? (
                    <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
                      <div>
                        <p className="text-lg font-semibold text-gray-900">Teams</p>
                        <p className="mt-1 text-sm text-gray-500">
                          Selecting a team adds its members into the participant list automatically.
                        </p>
                      </div>
                      <div className="mt-5 grid gap-3 md:grid-cols-2">
                        {teamsList.map((team) => {
                          const checked = form.selectedTeamIds.includes(team.id)
                          return (
                            <label
                              key={team.id}
                              className="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 transition hover:border-gray-300 hover:bg-white"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  void handleTeamToggle(team.id, event.target.checked)
                                }}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#16c1b2] focus:ring-[#16c1b2]"
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-gray-900">{team.name}</span>
                                <span className="mt-1 block text-sm text-gray-500">
                                  {team.memberCount} member{team.memberCount === 1 ? '' : 's'}
                                </span>
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                    ) : null}

                    <div
                      className={builderPanelClassName(
                        Boolean(peopleInviteError),
                        'rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm'
                      )}
                    >
                    <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                      <input
                        type="text"
                        value={participantDraft.name}
                        onChange={(event) => setParticipantDraft((prev) => ({ ...prev, name: event.target.value }))}
                        onKeyDown={handleParticipantKeyDown}
                        placeholder="Guest name (optional)"
                        className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                      />
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={participantDraft.email}
                        onChange={(event) => {
                          setParticipantDraft((prev) => ({ ...prev, email: event.target.value }))
                          if (event.target.value.trim()) setParticipantDraftError(null)
                        }}
                        onKeyDown={handleParticipantKeyDown}
                        placeholder={`name@${userDomain}`}
                        aria-invalid={Boolean(participantDraftError)}
                        className={builderInputClassName(Boolean(participantDraftError), 'compact')}
                      />
                      <button
                        type="button"
                        onClick={handleAddParticipant}
                        className={builderCompactPrimaryButtonClassName()}
                      >
                        Add
                      </button>
                    </div>
                    <BuilderStepInlineError message={participantDraftError} />

                    <div className="mt-6 flex flex-wrap gap-2">
                      {form.participants.length > 0 ? form.participants.map((participant) => (
                        <ParticipantChip
                          key={participant.email}
                          participant={participant}
                          userDomain={userDomain}
                          onRemove={removeParticipant}
                        />
                      )) : (
                        <p className="text-sm text-gray-500">No participants added yet.</p>
                      )}
                    </div>
                    <BuilderStepInlineError message={peopleInviteError} />
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
                      <button
                        type="button"
                        onClick={goNext}
                        className="btn-primary inline-flex items-center gap-2"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </StepShell>
              ) : null}

              {!successState && currentStage === 'recurrence' ? (
                <StepShell
                  title="Is this one meeting or part of a series?"
                  contentTopClassName="mt-6 sm:mt-7"
                >
                  <div className="mx-auto grid w-full max-w-2xl gap-4 md:grid-cols-2">
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      iconAccentBackground
                      icon={<CalendarClock className="h-5 w-5" />}
                      title="One-off"
                      selected={form.recurrenceKind === 'one_off'}
                      onClick={() => advanceWithFormPatch({ recurrenceKind: 'one_off' })}
                    />
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      iconAccentBackground
                      icon={<Repeat className="h-5 w-5" />}
                      title="Recurring series"
                      selected={form.recurrenceKind === 'recurring'}
                      onClick={() => {
                        setStepInlineError(null)
                        setForm((prev) => ({
                          ...prev,
                          recurrenceKind: 'recurring',
                          recurrenceRule: { ...prev.recurrenceRule, repeat_interval: 1 },
                        }))
                      }}
                    />
                  </div>

                  {form.recurrenceKind === 'recurring' ? (
                    <>
                      <div
                        className="mt-8 w-full rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm"
                        data-mb-enter-group="mb-recurrence-step"
                      >
                        <label className="block max-w-md">
                          <span className="mb-2 block text-sm font-medium text-gray-700">Repeats</span>
                          <select
                            value={form.recurrenceRule.type}
                            onChange={(event) => {
                              setStepInlineError(null)
                              const nextType = Number(event.target.value)
                              setForm((prev) => {
                                const rule = prev.recurrenceRule ?? { ...DEFAULT_RECURRENCE_RULE }
                                const scheduledMatch = String(prev.scheduledAtLocal ?? '').match(
                                  /^(\d{4})-(\d{2})-(\d{2})/,
                                )
                                const fromScheduleDay = scheduledMatch
                                  ? Math.min(31, Math.max(1, Number(scheduledMatch[3])))
                                  : null
                                const defaultMonthDay = fromScheduleDay ?? new Date().getDate()
                                const prevMd = Number(rule.monthly_day)
                                const keepMonthDay =
                                  Number.isInteger(prevMd) && prevMd >= 1 && prevMd <= 31 ? prevMd : defaultMonthDay
                                let nextRule = {
                                  ...rule,
                                  type: nextType,
                                  repeat_interval: 1,
                                }
                                if (nextType === 1) {
                                  nextRule = { ...nextRule, weekly_days: '', monthly_day: '' }
                                } else if (nextType === 2) {
                                  nextRule = { ...nextRule, monthly_day: '' }
                                } else if (nextType === 3) {
                                  nextRule = {
                                    ...nextRule,
                                    weekly_days: '',
                                    monthly_day: keepMonthDay,
                                  }
                                }
                                return { ...prev, recurrenceRule: nextRule }
                              })
                            }}
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                          >
                            <option value={1}>Daily</option>
                            <option value={2}>Weekly</option>
                            <option value={3}>Monthly</option>
                          </select>
                        </label>

                        {Number(form.recurrenceRule.type) === 2 ? (
                          <div className="mt-5">
                            <p className="mb-2 text-center text-sm font-medium text-gray-700">Days of the week</p>
                            <p className="mb-3 text-center text-xs text-gray-500">Tap each day this series should run.</p>
                            <div className="flex flex-wrap gap-2" role="group" aria-label="Weekdays">
                              {WEEKDAY_OPTIONS.map((day) => {
                                const currentDays = String(form.recurrenceRule.weekly_days ?? '')
                                  .split(',')
                                  .filter(Boolean)
                                const selected = currentDays.includes(day.value)
                                return (
                                  <button
                                    key={day.value}
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => {
                                      setStepInlineError(null)
                                      setForm((prev) => {
                                        const rule = prev.recurrenceRule ?? { ...DEFAULT_RECURRENCE_RULE }
                                        const days = String(rule.weekly_days ?? '')
                                          .split(',')
                                          .filter(Boolean)
                                        const isOn = days.includes(day.value)
                                        const nextDays = isOn
                                          ? days.filter((v) => v !== day.value)
                                          : [...days, day.value]
                                        return {
                                          ...prev,
                                          recurrenceRule: {
                                            ...rule,
                                            weekly_days: nextDays.sort((a, b) => Number(a) - Number(b)).join(','),
                                          },
                                        }
                                      })
                                    }}
                                    className={durationQuickOptionPillClassName(selected)}
                                  >
                                    {day.label}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        ) : null}

                        {Number(form.recurrenceRule.type) === 3 ? (
                          <div className="mt-5">
                            <label className="block max-w-xs">
                              <span className="mb-2 block text-sm font-medium text-gray-700">Day of month</span>
                              <p className="mb-2 text-xs text-gray-500">
                                Recurs on this calendar date each month (matches Zoom monthly meetings).
                              </p>
                              <select
                                value={
                                  form.recurrenceRule.monthly_day === '' ||
                                  form.recurrenceRule.monthly_day == null
                                    ? ''
                                    : String(form.recurrenceRule.monthly_day)
                                }
                                onChange={(event) => {
                                  setStepInlineError(null)
                                  const v = event.target.value
                                  setForm((prev) => ({
                                    ...prev,
                                    recurrenceRule: {
                                      ...prev.recurrenceRule,
                                      monthly_day: v === '' ? '' : Number(v),
                                    },
                                  }))
                                }}
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                              >
                                {MONTH_DAY_OPTIONS.map((day) => (
                                  <option key={day} value={day}>
                                    {ordinalDayLabel(day)}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        ) : null}

                        <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                          <div className="min-w-0 flex-1 sm:max-w-md">
                            <BuilderDatePicker
                              label="End by"
                              placeholder="Pick when the series ends"
                              preferPlacement="above"
                              disabled={form.recurrenceRule.never_ends === true}
                              minDate={recurrenceEndMinDate}
                              value={String(form.recurrenceRule.end_date_time ?? '').slice(0, 10)}
                              onChange={(dateStr) => {
                                setStepInlineError(null)
                                setField('recurrenceRule', {
                                  ...form.recurrenceRule,
                                  end_date_time: dateStr
                                    ? new Date(`${dateStr}T23:59:59`).toISOString()
                                    : '',
                                })
                              }}
                            />
                          </div>
                          <label className="flex shrink-0 cursor-pointer items-center gap-3 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={form.recurrenceRule.never_ends === true}
                              onChange={(event) => setField('recurrenceRule', {
                                ...form.recurrenceRule,
                                never_ends: event.target.checked,
                                end_times: event.target.checked ? 0 : (form.recurrenceRule.end_times || 0),
                                end_date_time: event.target.checked ? '' : form.recurrenceRule.end_date_time,
                              })}
                              className="h-4 w-4 rounded border-gray-300 text-[#16c1b2] focus:ring-[#16c1b2]"
                            />
                            This series never ends
                          </label>
                        </div>
                      </div>

                      <div className="mt-8 flex flex-wrap justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => goNext()}
                          className="btn-primary inline-flex items-center gap-2"
                        >
                          Continue
                        </button>
                      </div>
                      <BuilderStepInlineError message={stepInlineError} className="mt-3" />
                    </>
                  ) : null}
                  {form.recurrenceKind !== 'recurring' ? (
                    <BuilderStepInlineError message={stepInlineError} className="mt-4" />
                  ) : null}
                </StepShell>
              ) : null}

              {!successState && currentStage === 'schedule' ? (
                <StepShell
                  title="When should it happen?"
                  contentTopClassName="mt-6 sm:mt-7"
                >
                  <div className="mx-auto grid w-full max-w-2xl gap-3 sm:grid-cols-2 sm:gap-4">
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      iconAccentBackground
                      selected={form.scheduleMode === 'now'}
                      icon={<Clock3 className="h-5 w-5" />}
                      title="Now"
                      onClick={() => {
                        setStepInlineError(null)
                        setField('scheduleMode', 'now')
                      }}
                    />
                    <ChoiceCard
                      centered
                      centeredSize="md"
                      iconAccentBackground
                      selected={form.scheduleMode === 'later'}
                      icon={<CalendarClock className="h-5 w-5" />}
                      title="Schedule it"
                      onClick={() => {
                        setStepInlineError(null)
                        selectScheduleModeLater()
                      }}
                    />
                  </div>
                  <BuilderStepInlineError message={stepInlineError} className="mt-4" />

                  {form.scheduleMode === 'now' || form.scheduleMode === 'later' ? (
                  <div className="mt-6 w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:rounded-3xl sm:p-5">
                    {form.scheduleMode === 'later' ? (
                      <div data-mb-enter-group="mb-schedule-datetime">
                        <ScheduleLaterDateTime
                          value={form.scheduledAtLocal}
                          timezone={form.timezone || getQuickStartOrganizerTimezone(user)}
                          onChange={handleScheduledLocalChange}
                          compact
                        />
                        <BuilderStepInlineError message={scheduleDateTimeError} className="mt-2" />
                      </div>
                    ) : (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm text-gray-700 sm:py-4 sm:text-base">
                        The meeting will use your current time when you create it.
                      </div>
                    )}

                    {/* Schedule conflict warning banner */}
                    {form.scheduleMode === 'later' && form.scheduledAtLocal && (
                      <ScheduleConflictBanner
                        overlapping={conflictOverlapping}
                        meetingBefore={conflictBefore}
                        meetingAfter={conflictAfter}
                        loading={conflictsLoading}
                      />
                    )}

                    <div className="mt-3 border-t border-gray-100 pt-3 sm:mt-4 sm:pt-4">
                      <p className="mb-3 text-center text-xs font-medium text-gray-700 sm:text-sm">
                        Duration (required)
                      </p>
                      <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                        {MEETING_DURATION_QUICK_OPTIONS.map((opt) => {
                          return (
                            <button
                              key={opt.minutes}
                              type="button"
                              aria-pressed={isAgendaDurationPresetSelected(form.durationMinutes, opt.minutes)}
                              onClick={() => advanceScheduleWithDurationPreset(opt.minutes)}
                              className={durationQuickOptionPillClassName(
                                isAgendaDurationPresetSelected(form.durationMinutes, opt.minutes)
                              )}
                            >
                              {opt.label}
                            </button>
                          )
                        })}
                      </div>
                      <div className="mt-4 flex flex-col items-center">
                        <DurationMinutesStepper
                          id="meeting-builder-schedule-duration-custom"
                          value={form.durationMinutes}
                          hasError={Boolean(scheduleDurationError)}
                          required
                          onChange={(next) => setField('durationMinutes', next)}
                          onEnter={() => goNext()}
                        />
                        <BuilderStepInlineError message={scheduleDurationError} className="mt-1.5 text-center" />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
                      <button type="button" onClick={() => goNext()} className="btn-primary inline-flex items-center gap-2">
                        Continue
                      </button>
                    </div>
                  </div>
                  ) : null}
                </StepShell>
              ) : null}

              {!successState && currentStage === 'agenda' ? (
                <StepShell
                  title="What needs to be on the agenda?"
                  contentTopClassName="mt-6 sm:mt-7"
                >
                  <div className="mx-auto w-full max-w-4xl space-y-2 sm:space-y-2.5">
                    {form.audienceMode === 'external' ? (
                      <p className="text-xs text-gray-500 sm:text-sm">Guests see these topics only.</p>
                    ) : null}
                    {(form.agendaItems ?? []).some((item) => String(item?.title ?? '').trim()) ? (
                      <MeetingInputQualityIndicator
                        compact
                        label="Agenda quality"
                        tier={agendaInputQuality.tier}
                        hint={AGENDA_QUALITY_HINTS[agendaInputQuality.tier]}
                      />
                    ) : null}
                    {form.audienceMode === 'external' && internalAgendaLoadError ? (
                      <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:text-sm">
                        {internalAgendaLoadError}
                      </p>
                    ) : null}
                    {form.audienceMode === 'external' && internalAgendaLoading ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 sm:text-sm">
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                        Loading
                      </div>
                    ) : null}
                    <div
                      className="max-h-[min(42vh,400px)] min-h-0 space-y-2 overflow-y-auto pr-0.5 sm:max-h-[min(48vh,460px)]"
                      data-mb-agenda-scroll
                    >
                      {form.agendaItems.map((item, index) => {
                        const hostGoal = internalGoals.find((g) => g.linkedPublicAgendaItemId === item.id)
                        const titleMissingError = agendaTitleErrorItemIds.includes(item.id)
                        const durationMissingError = agendaDurationErrorItemIds.includes(item.id)
                        return (
                        <div
                          key={item.id}
                          className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4"
                          data-mb-enter-group={`mb-agenda-item-${item.id}`}
                          data-mb-agenda-item-id={item.id}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-[#16c1b2] sm:text-xs">
                              {form.audienceMode === 'external' ? `Public topic ${index + 1}` : `Item ${index + 1}`}
                            </p>
                            {form.agendaItems.length > 1 ? (
                              <button
                                type="button"
                                onClick={() => removeAgendaItem(item.id)}
                                className="shrink-0 rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                aria-label={`Remove agenda item ${index + 1}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            ) : null}
                          </div>
                          <div className="mt-2 grid gap-2 sm:gap-2.5">
                            <input
                              type="text"
                              required
                              value={item.title}
                              data-mb-agenda-title-input
                              aria-invalid={titleMissingError}
                              onChange={(event) => updateAgendaItem(item.id, { title: event.target.value })}
                              placeholder={
                                titleMissingError ? 'Add a title or remove this item' : 'Topic title'
                              }
                              className={agendaFieldClassName(titleMissingError)}
                            />
                            <textarea
                              value={item.details}
                              onChange={(event) => updateAgendaItem(item.id, { details: event.target.value })}
                              placeholder="Supporting notes (bullets help — who does what)"
                              rows={2}
                              aria-invalid={titleMissingError}
                              className={`min-h-[2.5rem] resize-y ${agendaFieldClassName(titleMissingError)}`}
                            />
                            <div className="flex items-start justify-between gap-3 pt-0.5">
                              <div className="min-w-0 pt-1">
                                {form.audienceMode === 'external' && !internalAgendaLoading && !hostGoal ? (
                                  <button
                                    type="button"
                                    onClick={() => addInternalGoalForAgendaItem(item.id)}
                                    data-mb-no-blur-advance
                                    className="text-sm font-medium text-gray-600 transition hover:text-gray-900 hover:underline"
                                  >
                                    Link Internal Goal
                                  </button>
                                ) : null}
                              </div>
                              <div className="shrink-0">
                                <AgendaItemUploadButton
                                  meetingId={attachmentMeetingId}
                                  listScope={attachmentListScope}
                                  preparing={attachmentMeetingProvisioning}
                                  onEnsureMeeting={ensureServerMeetingForAttachments}
                                  onUploaded={bumpAttachmentsSummary}
                                  disabled={!form.title.trim()}
                                  disabledTitle="Add a meeting title in Step 3 to upload documents."
                                />
                              </div>
                            </div>
                            {form.audienceMode === 'external' && hostGoal ? (
                              <div className="space-y-2" data-mb-no-blur-advance>
                                <div className="grid gap-2 sm:grid-cols-2">
                                  <select
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-2 focus:ring-[#16c1b2]/10"
                                    value={hostGoal.desiredOutcome}
                                    onChange={(e) =>
                                      updateInternalGoalRow(hostGoal.internalAgendaItemId, {
                                        desiredOutcome: e.target.value,
                                      })
                                    }
                                  >
                                    {INTERNAL_DESIRED_OUTCOMES.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-2 focus:ring-[#16c1b2]/10"
                                    value={hostGoal.visibilityScope === 'member_private' ? 'member_private' : 'shared_internal_team'}
                                    onChange={(e) =>
                                      updateInternalGoalRow(hostGoal.internalAgendaItemId, {
                                        visibilityScope: e.target.value,
                                        authorUserId:
                                          e.target.value === 'member_private' ? String(user?.id ?? '') || null : null,
                                      })
                                    }
                                  >
                                    <option value="shared_internal_team">Team</option>
                                    <option value="member_private">Only me</option>
                                  </select>
                                </div>
                                <textarea
                                  value={hostGoal.privateNotes}
                                  onChange={(e) =>
                                    updateInternalGoalRow(hostGoal.internalAgendaItemId, {
                                      privateNotes: e.target.value,
                                    })
                                  }
                                  rows={2}
                                  placeholder="Private notes (optional)"
                                  className="min-h-[2.5rem] w-full resize-y rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-2 focus:ring-[#16c1b2]/10"
                                />
                                <div className="flex justify-start">
                                  <button
                                    type="button"
                                    onClick={() => removeInternalGoalRow(hostGoal.internalAgendaItemId)}
                                    className="text-sm font-medium text-gray-600 transition hover:text-gray-900 hover:underline"
                                  >
                                    Unlink internal goal
                                  </button>
                                </div>
                              </div>
                            ) : null}
                            <div>
                              <p className="mb-2 text-center text-xs font-medium text-gray-600">Item Duration</p>
                              <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                                {AGENDA_TOPIC_DURATION_QUICK_OPTIONS.map((opt) => {
                                  const durationSelected = isAgendaDurationPresetSelected(
                                    item.durationMinutes,
                                    opt.minutes
                                  )
                                  return (
                                    <button
                                      key={`${item.id}-${opt.minutes}`}
                                      type="button"
                                      aria-pressed={durationSelected}
                                      onClick={() =>
                                        updateAgendaItem(item.id, { durationMinutes: String(opt.minutes) })
                                      }
                                      className={durationQuickOptionPillClassName(durationSelected)}
                                    >
                                      {opt.label}
                                    </button>
                                  )
                                })}
                              </div>
                              <div className="mt-3 flex flex-col items-center gap-1.5">
                                <DurationMinutesStepper
                                  id={`mb-agenda-duration-${item.id}`}
                                  size="compact"
                                  label="Custom (min.)"
                                  value={item.durationMinutes}
                                  hasError={durationMissingError}
                                  onChange={(next) =>
                                    updateAgendaItem(item.id, { durationMinutes: next })
                                  }
                                />
                                {!item.durationMinutes && meetingDuration > 0 ? (
                                  <span
                                    className={`text-xs font-medium sm:text-sm ${remainingMeetingMinutes > 0 ? 'text-gray-400' : 'text-red-400'}`}
                                  >
                                    {remainingMeetingMinutes > 0
                                      ? `${remainingMeetingMinutes} min left`
                                      : 'No time left'}
                                  </span>
                                ) : null}
                                {item.durationMinutes && meetingDuration > 0 && totalAgendaMinutes > meetingDuration ? (
                                  <span className="text-xs font-medium text-red-400 sm:text-sm">
                                    Over by {totalAgendaMinutes - meetingDuration} min
                                  </span>
                                ) : null}
                              </div>
                              <BuilderStepInlineError
                                message={durationMissingError ? AGENDA_DURATION_ERROR : null}
                                className="mt-1 text-center"
                              />
                            </div>
                          </div>
                        </div>
                        )
                      })}
                    </div>

                    {attachmentMeetingId ? (
                      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#16c1b2]">
                          Meeting documents
                        </p>
                        <MeetingAttachmentsSummaryList
                          key={attachmentsSummaryVersion}
                          meetingId={attachmentMeetingId}
                          listScope={attachmentListScope}
                          allowDelete
                          onMutated={bumpAttachmentsSummary}
                        />
                      </div>
                    ) : null}

                    <BuilderStepInlineError
                      message={agendaTimeBudgetError}
                      className="rounded-xl border border-red-200 bg-red-50/60 px-3 py-2 text-sm"
                    />

                    <div className="flex flex-wrap gap-2 pt-0.5">
                      <button
                        type="button"
                        onClick={addAgendaItem}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add agenda item
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
                      <button
                        type="button"
                        onClick={goNext}
                        className="btn-primary inline-flex items-center gap-2"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </StepShell>
              ) : null}

              {!successState && currentStage === 'review' ? (
                <StepShell variant="review" title="Review & Finalize">
                  <div className="mx-auto w-full max-w-xl space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      {existingMeeting?.isDraft !== false ? (
                        <button
                          type="button"
                          onClick={() => setShowDeleteDraftConfirm(true)}
                          disabled={deletingDraft || submitting || savingDraft}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete Draft
                        </button>
                      ) : (
                        <span aria-hidden="true" />
                      )}
                      <button
                        type="button"
                        onClick={() => setReviewEditing((v) => !v)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${reviewEditing ? 'bg-[#e4faf8] text-[#0d8f84] hover:bg-[#c0ede9]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {reviewEditing ? 'Done editing' : 'Edit meeting'}
                      </button>
                    </div>

                    {reviewEditing ? (
                      <>
                        {/* ── Editable mode ── */}
                        {/* Title */}
                        <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#16c1b2] sm:text-sm">Title</p>
                          <input
                            type="text"
                            value={form.title}
                            onChange={(e) => setField('title', e.target.value)}
                            placeholder="Meeting title"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                          />
                        </div>

                        {/* Schedule & Duration */}
                        <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#16c1b2] sm:text-sm">Schedule &amp; Duration</p>
                          <div className="mx-auto grid w-full max-w-2xl gap-3 sm:grid-cols-2 sm:gap-4">
                            <ChoiceCard
                              centered
                              centeredSize="md"
                              selected={form.scheduleMode === 'now'}
                              icon={<Clock3 className="h-5 w-5" />}
                              title="Now"
                              onClick={() => setField('scheduleMode', 'now')}
                            />
                            <ChoiceCard
                              centered
                              centeredSize="md"
                              selected={form.scheduleMode === 'later'}
                              icon={<CalendarClock className="h-5 w-5" />}
                              title="Schedule it"
                              onClick={selectScheduleModeLater}
                            />
                          </div>
                          <div className="mt-3">
                            {form.scheduleMode === 'later' ? (
                              <div>
                                <ScheduleLaterDateTime
                                  value={form.scheduledAtLocal}
                                  timezone={form.timezone || getQuickStartOrganizerTimezone(user)}
                                  onChange={handleScheduledLocalChange}
                                  compact
                                />
                                {form.scheduledAtLocal && <ScheduleConflictBanner overlapping={conflictOverlapping} meetingBefore={conflictBefore} meetingAfter={conflictAfter} loading={conflictsLoading} />}
                              </div>
                            ) : (
                              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm text-gray-700 sm:py-4 sm:text-base">
                                The meeting will use your current time when you create it.
                              </div>
                            )}
                          </div>
                          <motion.div className="mt-3 border-t border-gray-100 pt-3 sm:mt-4 sm:pt-4">
                            <p className="mb-3 text-center text-xs font-medium text-gray-700 sm:text-sm">Duration</p>
                            <motion.div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                              {MEETING_DURATION_QUICK_OPTIONS.map((opt) => (
                                <button
                                  key={opt.minutes}
                                  type="button"
                                  aria-pressed={isAgendaDurationPresetSelected(form.durationMinutes, opt.minutes)}
                                  onClick={() => setField('durationMinutes', String(opt.minutes))}
                                  className={durationQuickOptionPillClassName(
                                    isAgendaDurationPresetSelected(form.durationMinutes, opt.minutes)
                                  )}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </motion.div>
                            <motion.div className="mt-4 flex flex-col items-center">
                              <DurationMinutesStepper
                                id="meeting-builder-review-duration-custom"
                                value={form.durationMinutes}
                                onChange={(next) => setField('durationMinutes', next)}
                              />
                            </motion.div>
                          </motion.div>
                        </div>

                        {/* People */}
                        <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#16c1b2] sm:text-sm">People</p>
                          {teamsList.length > 0 && (
                            <div className="mb-4">
                              <p className="mb-2 text-xs font-medium text-gray-700 sm:text-sm">Teams</p>
                              <div className="grid gap-2 sm:grid-cols-2">
                                {teamsList.map((team) => {
                                  const checked = form.selectedTeamIds.includes(team.id)
                                  return (
                                    <label key={team.id} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 transition hover:border-gray-300 hover:bg-white">
                                      <input type="checkbox" checked={checked} onChange={(e) => { void handleTeamToggle(team.id, e.target.checked) }} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#16c1b2] focus:ring-[#16c1b2]" />
                                      <span className="min-w-0">
                                        <span className="block text-sm font-semibold text-gray-900">{team.name}</span>
                                        <span className="block text-xs text-gray-500">{team.memberCount} member{team.memberCount === 1 ? '' : 's'}</span>
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                            <input type="text" value={participantDraft.name} onChange={(e) => setParticipantDraft((prev) => ({ ...prev, name: e.target.value }))} onKeyDown={handleParticipantKeyDown} placeholder="Guest name (optional)" className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10" />
                            <input
                              type="email"
                              inputMode="email"
                              autoComplete="email"
                              value={participantDraft.email}
                              onChange={(e) => {
                                setParticipantDraft((prev) => ({ ...prev, email: e.target.value }))
                                if (e.target.value.trim()) setParticipantDraftError(null)
                              }}
                              onKeyDown={handleParticipantKeyDown}
                              placeholder={`name@${userDomain}`}
                              aria-invalid={Boolean(participantDraftError)}
                              className={builderInputClassName(Boolean(participantDraftError), 'compact')}
                            />
                            <button type="button" onClick={handleAddParticipant} className={builderCompactPrimaryButtonClassName()}>
                              Add
                            </button>
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {form.participants.length > 0 ? form.participants.map((participant) => (
                              <ParticipantChip
                                key={participant.email}
                                participant={participant}
                                userDomain={userDomain}
                                onRemove={removeParticipant}
                              />
                            )) : <p className="text-sm text-gray-500">No participants added yet.</p>}
                          </div>
                          <BuilderStepInlineError message={participantDraftError} />
                        </div>

                        {/* Agenda */}
                        <div className="rounded-[28px] border border-gray-200 bg-white p-5 text-left shadow-sm sm:p-6">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#16c1b2] sm:text-sm">{form.audienceMode === 'external' ? 'Agenda & Internal Goals' : 'Agenda'}</p>
                          <div
                      className="max-h-[min(42vh,400px)] min-h-0 space-y-2 overflow-y-auto pr-0.5 sm:max-h-[min(48vh,460px)]"
                      data-mb-agenda-scroll
                    >
                            {form.agendaItems.map((item, index) => (
                              <div key={item.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-[#16c1b2] sm:text-xs">Item {index + 1}</p>
                                  {form.agendaItems.length > 1 ? <button type="button" onClick={() => removeAgendaItem(item.id)} className="shrink-0 rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700" aria-label={`Remove agenda item ${index + 1}`}><X className="h-3.5 w-3.5" /></button> : null}
                                </div>
                                <div className="mt-2 grid gap-2 sm:gap-2.5">
                                  <input type="text" value={item.title} onChange={(e) => updateAgendaItem(item.id, { title: e.target.value })} placeholder="Topic title" className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:ring-2 focus:ring-[#16c1b2]/10" />
                                  <textarea value={item.details} onChange={(e) => updateAgendaItem(item.id, { details: e.target.value })} placeholder="Optional notes" rows={2} className="min-h-[2.5rem] resize-y rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:ring-2 focus:ring-[#16c1b2]/10" />
                                  <div className="flex justify-end pt-0.5">
                                    <AgendaItemUploadButton
                                      meetingId={attachmentMeetingId}
                                      listScope={attachmentListScope}
                                      preparing={attachmentMeetingProvisioning}
                                      onEnsureMeeting={ensureServerMeetingForAttachments}
                                      onUploaded={bumpAttachmentsSummary}
                                      disabled={!form.title.trim()}
                                      disabledTitle="Add a meeting title to upload documents."
                                    />
                                  </div>
                                  <div>
                                    <p className="mb-2 text-center text-xs font-medium text-gray-600">Item Duration</p>
                                    <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                                      {AGENDA_TOPIC_DURATION_QUICK_OPTIONS.map((opt) => {
                                        const durationSelected = isAgendaDurationPresetSelected(
                                          item.durationMinutes,
                                          opt.minutes
                                        )
                                        return (
                                          <button
                                            key={`${item.id}-${opt.minutes}`}
                                            type="button"
                                            aria-pressed={durationSelected}
                                            onClick={() =>
                                              updateAgendaItem(item.id, { durationMinutes: String(opt.minutes) })
                                            }
                                            className={durationQuickOptionPillClassName(durationSelected)}
                                          >
                                            {opt.label}
                                          </button>
                                        )
                                      })}
                                    </div>
                                    <div className="mt-3 flex flex-col items-center gap-1.5">
                                      <DurationMinutesStepper
                                        id={`mb-review-agenda-duration-${item.id}`}
                                        size="compact"
                                        label="Custom (min.)"
                                        value={item.durationMinutes}
                                        onChange={(next) =>
                                          updateAgendaItem(item.id, { durationMinutes: next })
                                        }
                                      />
                                      {!item.durationMinutes && meetingDuration > 0 ? (
                                        <span
                                          className={`text-xs font-medium sm:text-sm ${remainingMeetingMinutes > 0 ? 'text-gray-400' : 'text-red-400'}`}
                                        >
                                          {remainingMeetingMinutes > 0
                                            ? `${remainingMeetingMinutes} min left`
                                            : 'No time left'}
                                        </span>
                                      ) : null}
                                      {item.durationMinutes && meetingDuration > 0 && totalAgendaMinutes > meetingDuration ? (
                                        <span className="text-xs font-medium text-red-400 sm:text-sm">
                                          Over by {totalAgendaMinutes - meetingDuration} min
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={addAgendaItem}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add agenda item
                            </button>
                          </div>
                        </div>

                      </>
                    ) : (
                      <>
                        {/* ── Read-only summary ── */}
                        <div className="space-y-3">
                          <details open className="group overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm open:ring-1 open:ring-[#16c1b2]/30">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5 [&::-webkit-details-marker]:hidden">
                              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#16c1b2]">Summary</p>
                              <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" aria-hidden />
                            </summary>
                            <div className="border-t border-gray-100 px-4 pb-4 pt-3 sm:px-5 sm:pb-4">
                              <dl className="divide-y divide-gray-100 text-base text-gray-600">
                                <div className="flex items-baseline justify-between gap-4 py-2 first:pt-0">
                                  <dt className="shrink-0 font-semibold text-gray-900">Schedule</dt>
                                  <dd className="min-w-0 text-right">
                                    {form.scheduleMode === 'now'
                                      ? `Starts now · ${effectiveDurationMinutes} minutes`
                                      : `${formatInTimezone(resolveSchedulingFields({ forDraft: true }).scheduledStartTime, form.timezone)} · ${effectiveDurationMinutes} minutes`}
                                  </dd>
                                </div>
                                <div className="flex items-baseline justify-between gap-4 py-2">
                                  <dt className="shrink-0 font-semibold text-gray-900">Title</dt>
                                  <dd className="min-w-0 text-right">{form.title || 'Untitled meeting'}</dd>
                                </div>
                                <div className="flex items-baseline justify-between gap-4 py-2">
                                  <dt className="shrink-0 font-semibold text-gray-900">Meeting type</dt>
                                  <dd className="min-w-0 text-right">{reviewMeetingTypeName}</dd>
                                </div>
                                <div className="flex items-baseline justify-between gap-4 py-2">
                                  <dt className="shrink-0 font-semibold text-gray-900">Audience</dt>
                                  <dd className="min-w-0 text-right">{form.audienceMode === 'external' ? 'External-facing meeting' : 'Internal meeting'}</dd>
                                </div>
                                <div className="flex items-start justify-between gap-4 py-2">
                                  <dt className="shrink-0 pt-0.5 font-semibold text-gray-900">People</dt>
                                  <dd className="min-w-0 text-right">
                                    {form.participants.length === 0 && form.selectedTeamIds.length === 0 ? (
                                      <span className="text-gray-500">None</span>
                                    ) : (
                                      <div className={form.participants.length > 6 ? 'max-h-[min(32vh,14rem)] space-y-1.5 overflow-y-auto pr-0.5 text-right sm:max-h-[min(30vh,13rem)]' : 'space-y-1.5 text-right'}>
                                        {form.participants.map((participant, pIdx) => (
                                          <p key={participant.email || participant.id || `participant-${pIdx}`} className="font-medium text-gray-900">
                                            {participant.name || participant.email || 'Participant'}
                                            {participant.name && participant.email ? (
                                              <span className="font-normal text-gray-500"> · {participant.email}</span>
                                            ) : null}
                                          </p>
                                        ))}
                                        {form.selectedTeamIds.length > 0 ? (
                                          <p className="text-gray-600">
                                            Teams: {form.selectedTeamIds.map((id) => { const t = teamsList.find((team) => team.id === id); return t?.name?.trim() || t?.id || id }).join(', ')}
                                          </p>
                                        ) : null}
                                      </div>
                                    )}
                                  </dd>
                                </div>
                                <div className="flex items-baseline justify-between gap-4 py-2 last:pb-0">
                                  <dt className="shrink-0 font-semibold text-gray-900">Recurrence</dt>
                                  <dd className="min-w-0 text-right">{describeRecurrence(form.recurrenceKind, form.recurrenceRule)}</dd>
                                </div>
                              </dl>
                            </div>
                          </details>

                          <details className="group overflow-hidden rounded-[28px] border border-gray-200 bg-white text-left shadow-sm open:ring-1 open:ring-[#16c1b2]/30">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5 [&::-webkit-details-marker]:hidden">
                              <div className="min-w-0 text-left">
                                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#16c1b2]">
                                  {form.audienceMode === 'external' ? 'Agenda & Internal Goals' : 'Agenda'}
                                </p>
                                <p className="mt-0.5 text-base text-gray-600">
                                  {reviewListedAgendaItems.length} topic{reviewListedAgendaItems.length === 1 ? '' : 's'}
                                </p>
                              </div>
                              <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" aria-hidden />
                            </summary>
                            <div className="border-t border-gray-100 px-4 pb-4 pt-3 sm:px-5 sm:pb-4">
                              <div className={reviewListedAgendaItems.length > 5 ? 'max-h-[min(40vh,18rem)] space-y-3 overflow-y-auto pr-0.5 sm:max-h-[min(38vh,16rem)]' : 'space-y-3'}>
                                {reviewListedAgendaItems.map((item, index) => {
                                  const g = form.audienceMode === 'external' ? internalGoals.find((ig) => ig.linkedPublicAgendaItemId === item.id) : null
                                  const hostLine = (() => {
                                    if (!g) return null
                                    const { detail } = getReviewInternalGoalCardLines(g, form.agendaItems)
                                    if (detail) return detail
                                    const notes = (g.privateNotes || '').trim()
                                    if (notes) return notes
                                    return INTERNAL_DESIRED_OUTCOMES.find((o) => o.value === (g.desiredOutcome || 'gather_information'))?.label
                                  })()
                                  return (
                                    <div key={item.id} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                                      <p className="font-medium text-gray-900">{index + 1}. {item.title}</p>
                                      {item.details ? <p className="mt-1 text-base text-gray-500">{item.details}</p> : null}
                                      {hostLine ? <p className="mt-2 border-t border-gray-200/80 pt-2 text-sm text-gray-500">Internal Goals: {hostLine}</p> : null}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </details>

                          <details className="group overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm open:ring-1 open:ring-[#16c1b2]/30">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5 [&::-webkit-details-marker]:hidden">
                              <div className="min-w-0 text-left">
                                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#16c1b2]">
                                  Meeting documents
                                </p>
                                <p className="mt-0.5 text-base text-gray-600">
                                  {attachmentMeetingId ? 'Prep materials for this meeting' : 'None yet'}
                                </p>
                              </div>
                              <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" aria-hidden />
                            </summary>
                            <div className="border-t border-gray-100 px-4 pb-4 pt-3 sm:px-5 sm:pb-4">
                              <MeetingAttachmentsSummaryList
                                key={attachmentsSummaryVersion}
                                meetingId={attachmentMeetingId}
                                listScope={attachmentListScope}
                                allowDelete
                                onMutated={bumpAttachmentsSummary}
                              />
                            </div>
                          </details>
                        </div>
                      </>
                    )}

                    {/* Create / Save button */}
                    <BuilderStepInlineError message={stepInlineError} className="text-center" />
                    <div className="flex justify-center pt-1">
                      {existingMeeting?.isDraft === false && !existingMeeting.isLocalOnly ? (
                        <button
                          type="button"
                          onClick={handleRequestSendUpdate}
                          disabled={submitting || savingDraft || sendingUpdate}
                          className="btn-primary inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold shadow-sm disabled:cursor-not-allowed"
                        >
                          {sendingUpdate ? (
                            <>
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Mail className="h-4 w-4 shrink-0" />
                              Save &amp; Send Update
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={submitting || savingDraft}
                          className="btn-primary inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold shadow-sm disabled:cursor-not-allowed"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4 shrink-0" />
                              Create Meeting
                            </>
                          )}
                        </button>
                      )}
                    </div>

                  </div>
                </StepShell>
              ) : null}
            </AnimatePresence>
            </div>
          </div>
        </div>
      </main>

      {showDeleteDraftConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => !deletingDraft && setShowDeleteDraftConfirm(false)}
        >
          <div
            className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete draft?</h3>
                <p className="mt-1 text-sm text-gray-600">
                  This will permanently remove this draft and cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteDraftConfirm(false)}
                disabled={deletingDraft}
                className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleDeleteDraft() }}
                disabled={deletingDraft}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deletingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Delete Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {showSendUpdateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSendUpdateConfirm(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#e4faf8] flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-[#16c1b2]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Send update to participants?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will save your changes and send an updated calendar invite to{' '}
                  {form.participants.length === 1
                    ? '1 participant'
                    : `all ${form.participants.length} participants`}
                  {' '}via email.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button
                type="button"
                onClick={() => setShowSendUpdateConfirm(false)}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSendUpdate}
                autoFocus
                className="btn-primary px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Yes, save &amp; notify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MeetingBuilderPage
